import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  FiGrid, FiUser, FiCalendar, FiClock,
  FiDollarSign, FiFileText, FiStar, FiBell, FiSettings, FiLogOut, FiTrash2,
  FiMail, FiPhone, FiMapPin, FiBriefcase, FiVideo, FiSearch, FiDownload, FiCheckCircle, FiShield,
  FiTrendingUp, FiUsers, FiMessageCircle, FiCreditCard, FiGlobe, FiAlertTriangle,
  FiChevronLeft, FiChevronRight
} from "react-icons/fi";
import "./ExpertDashboard.css";
import { doc, updateDoc, serverTimestamp, onSnapshot, collection, query, where, orderBy, addDoc, getDoc, getDocs, setDoc, increment } from "firebase/firestore";
import { db, auth } from "../firebase";
import logoImg from "./Sorted_Logo.png";
// emailjs removed — cancellation notifications now go through the backend route
import { checkProfanity } from "./chatFilter";
import { io as socketIO } from "socket.io-client";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.thesorted.co.in";
const SOCKET_URL   = API_BASE_URL;

const SLOT_MINUTES = 30;

// Same list & selection cap used on the Expert Signup form, so editing a profile
// offers the identical set of industries.
const INDUSTRY_OPTIONS = [
  "Technology & SaaS", "Finance & Fintech", "Healthcare",
  "E-commerce & Retail", "Marketing & Growth", "Legal & Compliance",
  "HR & Talent", "Product Management", "Design & UX",
  "Operations & Supply Chain", "Education & EdTech", "Real Estate"
];
const MAX_INDUSTRY_SELECTIONS = 2;
const MAX_BIO_WORDS = 200;
const MAX_BIO_CHARS = 2000;

function parseSlotDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  try {
    const CleanedTime = timeStr.replace(/IST/g, '').trim();
    const [time, meridian] = CleanedTime.split(" ");
    let [h, m] = time.split(":").map(Number);
    if (meridian === "PM" && h !== 12) h += 12;
    if (meridian === "AM" && h === 12) h = 0;
    const targetDate = new Date(dateStr);
    targetDate.setHours(h, m, 0, 0);
    return targetDate;
  } catch (err) {
    console.error("Error parsing date/time:", err);
    return null;
  }
}

function formatTimeUntil(targetDate) {
  const diff = targetDate.getTime() - Date.now();
  if (diff <= 0) return "Starting now";
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `in ${hrs}h ${mins % 60}m`;
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function initialsOf(name) {
  if (!name) return "U";
  return name.split(" ").filter(Boolean).map(n => n[0]).join("").slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = ["#0d9488", "#6366f1", "#a855f7", "#ec4899", "#f59e0b", "#0ea5e9", "#22c55e"];
function avatarColorOf(name) {
  if (!name) return AVATAR_COLORS[0];
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function formatDateKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function next30Days() {
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < 30; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d);
  }
  return days;
}

function addMinutesToTimeStr(timeStr, minsToAdd) {
  const [time, meridian] = timeStr.split(" ");
  let [h, m] = time.split(":").map(Number);
  if (meridian === "PM" && h !== 12) h += 12;
  if (meridian === "AM" && h === 12) h = 0;
  let totalMins = h * 60 + m + minsToAdd;
  totalMins = ((totalMins % 1440) + 1440) % 1440;
  let nh = Math.floor(totalMins / 60);
  let nm = totalMins % 60;
  const newMeridian = nh >= 12 ? "PM" : "AM";
  let displayH = nh % 12;
  if (displayH === 0) displayH = 12;
  return `${displayH}:${String(nm).padStart(2, "0")} ${newMeridian}`;
}

// Sorting slot labels (e.g. "10:00 AM – 10:30 AM") as plain strings is wrong —
// "10:00 AM" alphabetically comes before "9:00 AM". This derives the real
// start-of-day minute count so slots always sort in actual chronological order.
function slotSortKey(label) {
  const startPart = (label.split("–")[0] || "").trim();
  const [time, meridian] = startPart.split(" ");
  if (!time || !meridian) return 0;
  let [h, m] = time.split(":").map(Number);
  if (meridian === "PM" && h !== 12) h += 12;
  if (meridian === "AM" && h === 12) h = 0;
  return h * 60 + (m || 0);
}

function isSlotPast(dateKey, slotLabel) {
  if (dateKey !== formatDateKey(new Date())) return false;
  const startPart = (slotLabel.split("–")[0] || "").trim();
  const startDate = parseSlotDateTime(dateKey, startPart);
  return startDate ? startDate.getTime() <= Date.now() : false;
}

/* ---------------- Simple SVG line chart (no external chart library) ---------------- */
function LineChartSVG({ points, formatY }) {
  if (!points || points.length === 0) {
    return <div className="chart-empty-state">No data yet</div>;
  }
  const width = 560, height = 220, padL = 50, padB = 28, padT = 14, padR = 10;
  const maxVal = Math.max(...points.map(p => p.value), 1);
  const stepX = (width - padL - padR) / Math.max(points.length - 1, 1);
  const coords = points.map((p, i) => {
    const x = padL + i * stepX;
    const y = padT + (height - padT - padB) * (1 - p.value / maxVal);
    return { x, y, ...p };
  });
  const linePath = coords.map((c, i) => (i === 0 ? `M${c.x},${c.y}` : `L${c.x},${c.y}`)).join(" ");
  const areaPath = `${linePath} L${coords[coords.length - 1].x},${height - padB} L${coords[0].x},${height - padB} Z`;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(maxVal * f));

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="line-chart-svg">
      {yTicks.map((t, i) => {
        const y = padT + (height - padT - padB) * (1 - t / maxVal);
        return (
          <g key={i}>
            <line x1={padL} x2={width - padR} y1={y} y2={y} stroke="#eef2f1" strokeDasharray="4 4" />
            <text x={padL - 8} y={y + 4} textAnchor="end" className="chart-axis-label">{formatY ? formatY(t) : t}</text>
          </g>
        );
      })}
      <path d={areaPath} fill="url(#revGradient)" opacity="0.5" />
      <defs>
        <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={linePath} fill="none" stroke="#0d9488" strokeWidth="2.5" />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r="3.5" fill="#0d9488" />
      ))}
      {coords.map((c, i) => (
        <text key={i} x={c.x} y={height - 8} textAnchor="middle" className="chart-axis-label">{c.label}</text>
      ))}
    </svg>
  );
}

function BarChartSVG({ points }) {
  if (!points || points.length === 0) {
    return <div className="chart-empty-state">No data yet</div>;
  }
  const width = 320, height = 220, padL = 28, padB = 28, padT = 14;
  const maxVal = Math.max(...points.map(p => p.value), 1);
  const barW = (width - padL - 10) / points.length * 0.55;
  const gap = (width - padL - 10) / points.length;
  const yTicks = 4;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="bar-chart-svg">
      {Array.from({ length: yTicks + 1 }).map((_, i) => {
        const val = Math.round((maxVal / yTicks) * i);
        const y = padT + (height - padT - padB) * (1 - i / yTicks);
        return (
          <g key={i}>
            <line x1={padL} x2={width - 5} y1={y} y2={y} stroke="#eef2f1" strokeDasharray="4 4" />
            <text x={padL - 6} y={y + 4} textAnchor="end" className="chart-axis-label">{val}</text>
          </g>
        );
      })}
      {points.map((p, i) => {
        const barH = (height - padT - padB) * (p.value / maxVal);
        const x = padL + i * gap + (gap - barW) / 2;
        const y = height - padB - barH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={Math.max(barH, 1)} rx="4" fill="#06b6d4" />
            <text x={x + barW / 2} y={height - 8} textAnchor="middle" className="chart-axis-label">{p.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function DonutChartSVG({ segments }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) {
    return <div className="donut-empty-ring"><span>0</span></div>;
  }
  const r = 60, cx = 70, cy = 70, strokeW = 18;
  const circumference = 2 * Math.PI * r;
  let offsetAcc = 0;
  return (
    <svg viewBox="0 0 140 140" className="donut-chart-svg">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f4" strokeWidth={strokeW} />
      {segments.filter(s => s.value > 0).map((seg, i) => {
        const fraction = seg.value / total;
        const dash = fraction * circumference;
        const circle = (
          <circle
            key={i}
            cx={cx} cy={cy} r={r} fill="none"
            stroke={seg.color}
            strokeWidth={strokeW}
            strokeDasharray={`${dash} ${circumference - dash}`}
            strokeDashoffset={-offsetAcc}
            transform={`rotate(-90 ${cx} ${cy})`}
            strokeLinecap="butt"
          />
        );
        offsetAcc += dash;
        return circle;
      })}
      <text x={cx} y={cy + 6} textAnchor="middle" className="donut-center-label">{total}</text>
    </svg>
  );
}

/* ---------------------------------------------------------------------------- */

function ExpertChatView({ booking, expertUser, onBack }) {
  const [messages,      setMessages]      = useState([]);
  const [input,         setInput]         = useState("");
  const [sending,       setSending]       = useState(false);
  const [filterWarning, setFilterWarning] = useState("");
  const [chatEnded,     setChatEnded]     = useState(false);
  const bottomRef = React.useRef(null);
  const socketRef = React.useRef(null);

  const chatId       = booking?.bookingId || booking?.id || booking?.docId;
  const isPastSession = booking?.status === "completed" || booking?.status === "cancelled";

  useEffect(() => {
    if (!chatId) return;

    // Past session — fetch chatLog from backend once (no socket, no Firestore reads)
    if (isPastSession) {
      auth.currentUser?.getIdToken().then(idToken => {
        fetch(`${SOCKET_URL}/api/chat/history/${chatId}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        })
          .then(r => r.json())
          .then(data => { if (data.messages) setMessages(data.messages); })
          .catch(err => console.error("chat history fetch error:", err));
      });
      return;
    }

    // Live session — connect via WebSocket
    const socket = socketIO(SOCKET_URL, { transports: ["websocket", "polling"] });
    socketRef.current = socket;

    auth.currentUser?.getIdToken().then(idToken => {
      socket.emit("join-chat", { bookingId: chatId, idToken });
    });

    socket.on("chat-history",    ({ messages: h }) => setMessages(h));
    socket.on("new-message",     (msg)             => setMessages(prev => [...prev, msg]));
    socket.on("message-blocked", ({ message })     => setFilterWarning(`⚠️ ${message}`));
    socket.on("chat-ended",      ()                => setChatEnded(true));
    socket.on("chat-error",      ({ message })     => console.error("chat-error:", message));

    return () => { socket.disconnect(); socketRef.current = null; };
  }, [chatId, isPastSession]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = () => {
    if (isPastSession || chatEnded) return;
    const text = input.trim();
    if (!text || sending || !socketRef.current) return;

    const { blocked, reason } = checkProfanity(text);
    if (blocked) {
      setFilterWarning(reason === "emoji"
        ? "⚠️ Emojis cannot be sent in this chat. Please use only text."
        : "⚠️ Your message contains language that isn't allowed. Please rephrase and try again."
      );
      return;
    }
    setFilterWarning("");
    setInput("");
    socketRef.current.emit("send-message", { text });
  };

  // Expert ends the session — saves chatLog to Firestore via socket event
  const endChat = () => {
    if (socketRef.current) {
      socketRef.current.emit("end-chat");
    }
    setChatEnded(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div className="chat-view-container">
      <div className="chat-view-header">
        <button type="button" onClick={onBack} className="chat-back-btn">←</button>
        <div className="chat-client-avatar" style={{ background: avatarColorOf(booking?.clientName) }}>
          {booking?.clientName && booking.clientName !== "undefined" ? initialsOf(booking.clientName) : "CU"}
        </div>
        <div className="chat-header-meta">
          <h4 className="chat-client-title">
            {booking?.clientName && booking.clientName !== "undefined" ? booking.clientName : "Client"}
          </h4>
          <p className="chat-session-subtitle">
            {booking?.date ? booking.date : "Today"} • {booking?.time ? booking.time : "Live"}
          </p>
        </div>
        <span className={`chat-status-badge ${isPastSession ? "archived" : "live"}`}>
          {isPastSession ? "Archived" : "● Live"}
        </span>
      </div>

      <div className="chat-messages-body">
        {messages.length === 0 && (
          <div className="chat-empty-workspace">
            <p>
              {isPastSession
                ? "No messages were exchanged in this session."
                : `Say hello to ${booking?.clientName && booking.clientName !== "undefined" ? booking.clientName : "the client"}.`}
            </p>
            {booking.topic && (
              <div className="chat-topic-banner">
                <strong>Client's topic:</strong> {booking.topic}
              </div>
            )}
          </div>
        )}
        {messages.map((msg) => {
          const isMe = msg.senderRole === "expert";
          return (
            <div key={msg.id} className={`chat-message-row ${isMe ? "outgoing" : "incoming"}`}>
              <div className="chat-message-bubble">
                <p>{msg.text}</p>
                <span className="chat-message-time">
                  {msg.createdAt?.toDate?.()?.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) || ""}
                </span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {!isPastSession && !chatEnded ? (
        <>
          {filterWarning && <div className="chat-filter-alert">{filterWarning}</div>}
          <div className="chat-input-footer-bar">
            <textarea
              rows={1}
              placeholder="Type your reply..."
              value={input}
              onChange={(e) => { setInput(e.target.value); if (filterWarning) setFilterWarning(""); }}
              onKeyDown={handleKeyDown}
              className="chat-textarea-input"
            />
            <button onClick={sendMessage} disabled={!input.trim() || sending} className="chat-send-submit-btn">
              {sending ? "..." : "➤"}
            </button>
            <button onClick={endChat} className="chat-end-btn" title="End session and save chat">
              End
            </button>
          </div>
        </>
      ) : (
        <div className="chat-readonly-system-banner">
          {chatEnded ? "Session ended. Chat saved." : "This conversation has ended and is now read-only."}
        </div>
      )}
    </div>
  );
}

// Reusable: a horizontally-scrolling row (day picker, filter pills) with
// left/right arrow buttons that nudge the scroll position, in addition to
// native touch/trackpad scrolling and the visible scrollbar. Defined at
// module scope (not inside ExpertDashboard) so it isn't re-created — and
// its child DOM remounted, losing scroll position — on every render; the
// scroll container's ref is passed in from the parent instead of owned
// internally.
function ScrollArrowRow({ className, scrollRef, children }) {
  const nudge = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 160, behavior: "smooth" });
    }
  };
  return (
    <div className={`scroll-arrow-row-wrapper ${className}-wrapper`}>
      <button type="button" className="scroll-arrow-btn left" onClick={() => nudge(-1)} aria-label="Scroll left">
        <FiChevronLeft size={16} />
      </button>
      <div className={className} ref={scrollRef}>
        {children}
      </div>
      <button type="button" className="scroll-arrow-btn right" onClick={() => nudge(1)} aria-label="Scroll right">
        <FiChevronRight size={16} />
      </button>
    </div>
  );
}

function ExpertDashboard({ user, onLogout }) {
  const availabilityScrollRef = useRef(null);
  const callsFilterScrollRef = useRef(null);

  // FIX: stable expertId — previously every useEffect used `user?.uid || user?.id`
  // inline, which re-evaluated on every render. When the parent passed a new
  // `user` object reference, all three onSnapshot useEffects would tear down
  // and restart, firing an immediate snapshot read each time. useMemo keyed
  // on the actual uid/id values means the string only changes when the identity
  // genuinely changes, not on every parent re-render.
  const expertId = useMemo(
    () => auth.currentUser?.uid || user?.uid || user?.id || null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.uid, user?.id]
  );

  // FIX: guards the onSnapshot callback from echoing back our own
  // saveAvailability write. Without this, every slot add/remove caused:
  // write → onSnapshot fires → setAvailabilityByDate → re-render → listener
  // restarts → immediate snapshot read → repeat. Visible as flicker.
  const savingAvailRef = useRef(false);
  // ---- Active sidebar tab + shared UI filters ----
  // Persisted per-user so a page refresh keeps you on the same tab instead
  // of always resetting to "dashboard".
  const activeTabStorageKey = `sorted_active_tab_${user?.uid || user?.id || "guest"}`;
  const [activeTab, setActiveTabState] = useState(() => {
    try {
      return localStorage.getItem(activeTabStorageKey) || "dashboard";
    } catch {
      return "dashboard";
    }
  });
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    try {
      localStorage.setItem(activeTabStorageKey, tab);
    } catch {
      // localStorage may be unavailable (private browsing, etc.) — safe to ignore
    }
  };
  // ---- Mobile-only UI state: hamburger drawer + bottom-nav "More" sheet ----
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const MOBILE_MORE_TABS = ["availability", "history", "reviews", "notifications", "settings"];
  const setActiveTabMobile = (tab) => {
    setActiveTab(tab);
    setMobileDrawerOpen(false);
    setMobileMoreOpen(false);
  };
  const [activeCallFilter, setActiveCallFilter] = useState("All");           // used by # Scheduled Calls
  const [historySearch, setHistorySearch] = useState("");                    // used by # Consultation History
  const [historyStatusFilter, setHistoryStatusFilter] = useState("All Status"); // used by # Consultation History

  // ---- # Dashboard / # Scheduled Calls / # Earnings / # Consultation History
  // (all derive from the same bookings data) ----
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [rawBookings, setRawBookings] = useState([]);
  // Cache of client profile docs (users/{uid}) keyed by uid, so we only have
  // to fetch each client's real name/company once even across many bookings.
  const [clientProfiles, setClientProfiles] = useState({});

  // ---- # Reviews ----
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  // ---- # Notifications ----
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [tick, forceTick] = useState(0);
  const [notificationFilter, setNotificationFilter] = useState("all"); // "all" | "unread"
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [readNotificationIds, setReadNotificationIds] = useState(() => {
    try {
      const storageKey = `sorted_read_notifications_${user?.uid || user?.id || "guest"}`;
      const raw = localStorage.getItem(storageKey);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  // ---- # Scheduled Calls: in-call chat + reschedule modal ----
  const [activeChatBooking, setActiveChatBooking] = useState(null);
  const [callDetailData, setCallDetailData] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({ show: false, bookingId: null, date: "", time: "" });
  const [cancelCallData, setCancelCallData] = useState({ show: false, call: null });

  // ---- # My Profile ----
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState(() => ({
    name: user?.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : user?.name || "",
    company: user?.company || "",
    email: user?.email || "",
    phone: user?.phone || "",
    location: user?.location || "",
    industry: Array.isArray(user?.industries) && user.industries.length
      ? user.industries
      : Array.isArray(user?.industry)
        ? user.industry
        : user?.industry
          ? String(user.industry).split(",").map(s => s.trim()).filter(Boolean)
          : [],
    experienceYears: user?.experienceYears || "",
    bio: user?.bio || "",
    profilePhoto: user?.profilePhoto || user?.profilePic || user?.documentsUrls?.profilePhoto || "",
    businessLogo: user?.businessLogo || "",
    bankEditStatus: user?.bankEditStatus || "locked",
    approvalStatus: user?.approvalStatus || "pending",
    consultationFee: user?.consultationFee ?? null,
    expertCutPercent: user?.expertCutPercent ?? 100, // % of consultationFee the expert actually earns (set by admin)
    isVisible: user?.isVisible || false
  }));

  // ---- # My Profile: bank details sub-section ----
  const [bankForm, setBankForm] = useState({
    holder: "", accountNum: "", confirmAccountNum: "", bankName: "", ifsc: "", upi: "", branch: "", location: "", docUrl: "", docName: ""
  });
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [bankRequestSent, setBankRequestSent] = useState(false);
  useEffect(() => { setBankRequestSent(false); }, [profileForm.bankEditStatus]);
  const [bankSuccessMsg, setBankSuccessMsg] = useState("");
  const [bankErrorMsg, setBankErrorMsg] = useState("");
  const [ifscLookupLoading, setIfscLookupLoading] = useState(false);
  const [bankDocFile, setBankDocFile] = useState(null);
  const [bankDocUploading, setBankDocUploading] = useState(false);

  // Accepts a passbook photo, cancelled cheque, or any document (image/PDF) showing
  // the account holder's name and account number. Just staged locally until save.
  const handleBankDocChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      setBankErrorMsg("Please upload a JPG, PNG, or PDF file.");
      e.target.value = "";
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      setBankErrorMsg("File is too large. Please upload a file under 30MB.");
      e.target.value = "";
      return;
    }
    setBankErrorMsg("");
    setBankDocFile(file);
  };

  // Clears the staged file or the previously uploaded document so the person can
  // pick a different one. Does not touch the file already saved in S3/Firestore
  // until "Save Changes" is pressed again with a new upload.
  const handleRemoveBankDoc = () => {
    setBankDocFile(null);
    setBankForm(prev => ({ ...prev, docUrl: "", docName: "" }));
  };

  // Looks up bank/branch details for a given IFSC code via Razorpay's public IFSC API
  // and fills them into the bank form. Safe to call repeatedly (e.g. on blur / on change).
  const getBankDetails = async (ifscCode) => {
    const code = (ifscCode || "").trim().toUpperCase();
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(code)) {
      return; // not a complete/valid IFSC yet, skip lookup silently
    }
    setIfscLookupLoading(true);
    setBankErrorMsg("");
    try {
      const response = await fetch(`https://ifsc.razorpay.com/${code}`);
      if (!response.ok) {
        throw new Error("Invalid IFSC Code or data not found");
      }
      const bankData = await response.json();
      const locationParts = [bankData.CITY, bankData.STATE].filter(Boolean);
      setBankForm(prev => ({
        ...prev,
        bankName: bankData.BANK || prev.bankName,
        branch: bankData.BRANCH || prev.branch,
        location: locationParts.length ? locationParts.join(", ") : prev.location
      }));
    } catch (error) {
      console.error("Error fetching bank details:", error.message);
      setBankErrorMsg("Could not verify IFSC code. Please check and try again.");
    } finally {
      setIfscLookupLoading(false);
    }
  };

  // ---- # Availability: rolling 30-day window from today, persisted by date key ----
  const [availabilityByDate, setAvailabilityByDate] = useState({});
  const [selectedDateKey, setSelectedDateKey] = useState(formatDateKey(new Date()));
  const [addSlotFormOpen, setAddSlotFormOpen] = useState(false);
  const [addSlotStartTime, setAddSlotStartTime] = useState([]);
  const dayList = next30Days();

  // ---- # Settings ----
  const [settingsForm, setSettingsForm] = useState({
    newBookings: true,
    sessionReminders: true,
    payoutNotifications: true,
    newReviews: true,
    platformUpdates: false,
    publicProfile: true,
    showSessionRate: true,
    showReviews: true,
    language: "English"
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(0); // 0 = closed, 1 = first pop-up, 2 = second pop-up
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);

  // ═══════════════════════════════════════════════════════════════════
  // BROWSER / DEVICE BACK-BUTTON SUPPORT
  //
  // Everything here (switching sidebar sections, opening a call's detail
  // view, drilling into a specific Availability day, editing the profile,
  // opening a notification) happens via React state — there's no real page
  // navigation, so by default the back button has nothing to "undo" and
  // just leaves the site entirely. This wires those state changes into the
  // History API so back behaves the way people expect:
  //   1st back press  → closes whatever detail view is open (a call, a
  //                      notification, profile edit mode, a selected day)
  //                      and returns to that section's main view.
  //   2nd back press  → leaves the section and returns to the Dashboard
  //                      tab, no matter which section you were in.
  //   3rd back press  → (nothing left to undo) — normal browser back,
  //                      i.e. actually leaves the expert portal.
  // ═══════════════════════════════════════════════════════════════════
  const isPopNavRef = useRef(false);       // true while we're reacting to a back/forward press
  const navLevelRef = useRef("base");      // "base" (dashboard) | "tab" (section) | "detail" (sub-view)
  const prevDetailRef = useRef(null);
  const scheduledCallsRef = useRef([]);
  const notificationsRef = useRef([]);

  useEffect(() => { scheduledCallsRef.current = scheduledCalls; }, [scheduledCalls]);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);

  // Establish the base "dashboard" history entry once, on first mount.
  useEffect(() => {
    if (!window.history.state || !window.history.state.expertNav) {
      window.history.replaceState({ expertNav: true, level: "base", tab: "dashboard" }, "");
    }
    navLevelRef.current = "base";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tab-level: switching sidebar/bottom-nav sections. Leaving "dashboard"
  // pushes one entry; switching between other sections without returning
  // to dashboard first just replaces that same entry — so back always
  // takes you straight to the Dashboard, regardless of which section
  // you're in or how many you've hopped through.
  useEffect(() => {
    if (isPopNavRef.current) return; // this change came FROM a back/forward press

    if (activeTab === "dashboard") {
      if (navLevelRef.current !== "base") {
        window.history.replaceState({ expertNav: true, level: "base", tab: "dashboard" }, "");
        navLevelRef.current = "base";
      }
    } else {
      if (navLevelRef.current === "base") {
        window.history.pushState({ expertNav: true, level: "tab", tab: activeTab }, "");
      } else {
        window.history.replaceState({ expertNav: true, level: "tab", tab: activeTab }, "");
      }
      navLevelRef.current = "tab";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Detail-level: a specific call, a specific notification, profile edit
  // mode, or a specific Availability day all count as "a page opened
  // inside the section". Opening one pushes a single extra entry; opening
  // a different one (e.g. clicking another day) replaces it rather than
  // stacking, so one back press is always enough to return to the section.
  const detailSignature =
    callDetailData        ? `call:${callDetailData.id}` :
    selectedNotification   ? `notif:${selectedNotification.id}` :
    isEditingProfile       ? "profile-edit" :
    activeTab === "availability" ? `day:${selectedDateKey}` :
    null;

  useEffect(() => {
    if (isPopNavRef.current) {
      prevDetailRef.current = detailSignature;
      return;
    }
    if (navLevelRef.current === "base") { prevDetailRef.current = detailSignature; return; }

    if (detailSignature && detailSignature !== prevDetailRef.current) {
      if (navLevelRef.current === "tab") {
        window.history.pushState({ expertNav: true, level: "detail", tab: activeTab, detail: detailSignature }, "");
      } else {
        window.history.replaceState({ expertNav: true, level: "detail", tab: activeTab, detail: detailSignature }, "");
      }
      navLevelRef.current = "detail";
    } else if (!detailSignature && navLevelRef.current === "detail") {
      // Closed via an on-screen close button rather than the back button —
      // just stop tracking it as "open"; the stale forward entry is a
      // harmless no-op if the user ever navigates forward again.
      navLevelRef.current = "tab";
    }

    prevDetailRef.current = detailSignature;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailSignature]);

  // Handle the actual back/forward button (or swipe-back gesture) press.
  useEffect(() => {
    const handlePopState = (e) => {
      const state = e.state;
      isPopNavRef.current = true;

      if (!state || !state.expertNav || state.level === "base") {
        navLevelRef.current = "base";
        setActiveTabState("dashboard");
        setCallDetailData(null);
        setSelectedNotification(null);
        setIsEditingProfile(false);
      } else if (state.level === "tab") {
        navLevelRef.current = "tab";
        setActiveTabState(state.tab);
        setCallDetailData(null);
        setSelectedNotification(null);
        setIsEditingProfile(false);
      } else if (state.level === "detail") {
        navLevelRef.current = "detail";
        setActiveTabState(state.tab);
        if (state.detail?.startsWith("call:")) {
          const id = state.detail.slice(5);
          const found = scheduledCallsRef.current.find((c) => String(c.id) === id);
          setCallDetailData(found || null);
        } else if (state.detail?.startsWith("notif:")) {
          const id = state.detail.slice(6);
          const found = notificationsRef.current.find((n) => String(n.id) === id);
          setSelectedNotification(found || null);
        } else if (state.detail === "profile-edit") {
          setIsEditingProfile(true);
        } else if (state.detail?.startsWith("day:")) {
          setSelectedDateKey(state.detail.slice(4));
        }
      }

      setMobileDrawerOpen(false);
      setMobileMoreOpen(false);

      // Release the guard after this render cycle so subsequent genuine
      // user-driven state changes push/replace history normally again.
      setTimeout(() => { isPopNavRef.current = false; }, 0);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // ---- # In-app toast notifications (replaces native browser alert() pop-ups) ----
  const [toast, setToast] = useState(null); // { message, type: 'success' | 'error' }
  // FIX: useCallback so showToast has a stable reference across renders.
  // Without this, any useEffect that calls showToast would need it in its
  // deps array, which would cause re-runs on every render.
  const showToast = React.useCallback(
    (message, type = "success") => setToast({ message, type }),
    [] // setToast from useState is always stable — safe empty deps
  );
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3800);
    return () => clearTimeout(timer);
  }, [toast]);

  // ---- # My Profile: profile photo upload ----
  const [profilePhotoUploading, setProfilePhotoUploading] = useState(false);
  const [photoLoadError, setPhotoLoadError] = useState(false);
  // Reset the error flag whenever the photo URL itself changes (e.g. after a fresh upload).
  useEffect(() => { setPhotoLoadError(false); }, [profileForm.profilePhoto]);

  // If the expert logs back in after deactivating (bank details were wiped on deactivation),
  // quietly move their status from "deactivated" back to "pending" so they can re-verify.
  // approvalStatus is the single field this whole app reads/writes for approval
  // state now (signup, login, session restore, and this dashboard all agree on
  // it) — a separate "status" field used to exist alongside it and drift out
  // of sync.
  // FIX: use a ref guard so this runs only ONCE on mount, not on every
  // profileForm.approvalStatus change. Previously, every admin signal
  // delivery that updated profileForm triggered an extra getDoc here.
  const reactivationCheckedRef = React.useRef(false);
  useEffect(() => {
    if (reactivationCheckedRef.current) return;
    if (!expertId) return;
    reactivationCheckedRef.current = true;
    const checkReactivation = async () => {
      try {
        const snap = await getDoc(doc(db, "experts", expertId));
        if (!snap.exists()) return;
        const currentStatus = (snap.data().approvalStatus || "").toLowerCase().trim();
        if (currentStatus === "deactivated") {
          await updateDoc(doc(db, "experts", expertId), { approvalStatus: "pending" });
          showToast("Welcome back! Please re-verify your bank details to resume your account.", "success");
        }
      } catch (err) {
        console.error("Dashboard auto-reactivation failure:", err);
      }
    };
    checkReactivation();
  }, [expertId]);

  // ---- # Dashboard / # Earnings: aggregate stats ----
  const [stats, setStats] = useState({
    totalEarnings: 0,
    monthEarnings: 0,
    pendingPayout: 0,
    totalSessions: 0,
    rating: 0,
    reviewsCount: 0
  });


  // ── LOAD PROFILE ONCE ON MOUNT ──────────────────────────────────────────
  // Name, bio, bank details, fee — none of these change mid-session.
  // A one-time getDoc is correct here. Previously these were inside the
  // onSnapshot below, which meant a new profileForm object was created on
  // every Firestore delivery (availability save, stats write, anything) —
  // that caused the stats fetch loop running at sub-second speed.
  useEffect(() => {
    if (!expertId) return;
    getDoc(doc(db, "experts", expertId)).then(docSnap => {
      if (!docSnap.exists()) return;
      const d = docSnap.data();
      setProfileForm(prev => ({
        ...prev,
        name: d.firstName ? `${d.firstName} ${d.lastName || ""}`.trim() : prev.name,
        company: d.company || prev.company,
        location: d.location || prev.location,
        bio: d.bio || prev.bio,
        phone: d.phone || prev.phone,
        email: d.email || prev.email,
        industry: Array.isArray(d.industries) && d.industries.length
          ? d.industries
          : d.industry
            ? (Array.isArray(d.industry) ? d.industry : String(d.industry).split(",").map(s => s.trim()).filter(Boolean))
            : prev.industry,
        experienceYears: d.experienceYears ?? prev.experienceYears,
        profilePhoto: d.profilePhoto || d.profilePic || d.documentsUrls?.profilePhoto || prev.profilePhoto,
        bankEditStatus: d.bankEditStatus || "locked",
        approvalStatus: d.approvalStatus || "pending",
        consultationFee: d.consultationFee ?? prev.consultationFee ?? null,
        expertCutPercent: d.expertCutPercent ?? prev.expertCutPercent ?? 100,
        isVisible: d.isVisible || false,
      }));
      if (d.bankDetailsAdded && d.bankHolder) {
        setBankForm(prev => ({
          ...prev,
          holder: d.bankHolder || "",
          bankName: d.bankName || "",
          branch: d.bankBranch || "",
          location: d.bankLocation || "",
          ifsc: d.bankIfsc || "",
          accountNum: d.bankAccountLast4 ? `••••••••${d.bankAccountLast4}` : prev.accountNum,
          confirmAccountNum: d.bankAccountLast4 ? `••••••••${d.bankAccountLast4}` : prev.confirmAccountNum,
          docUrl: d.bankDocUrl || prev.docUrl,
          docName: d.bankDocName || prev.docName,
        }));
      }
      if (d.availability && typeof d.availability === "object") {
        setAvailabilityByDate(d.availability);
      }
    }).catch(err => console.error("Failed to load expert profile:", err));
  }, [expertId]);

  // ── ON-DEMAND LISTENER — admin approval signal watcher ──────────────────
  // Replaces the permanent onSnapshot on experts/{expertId}.
  //
  // Instead of watching the full expert doc forever (which fired on every
  // field write — profile saves, photo uploads, slot changes — causing
  // unnecessary re-renders and contributing to the read/write loop),
  // we now watch ONE tiny signal doc: signals/{expertId}_admin
  //
  // AdminDashboard writes this signal after every approval action
  // (profile approve, bank approve, fee set). When the signal fires:
  //   1. Open a live listener on experts/{expertId} for 1 second
  //   2. Get the fresh admin-controlled fields
  //   3. Close the listener
  //   4. Show a toast so the expert knows something changed
  //
  // savingAvailRef still guards availability updates from the expert's
  // own slot saves (unchanged).
  useEffect(() => {
    if (!expertId) return;

    const applyExpertDoc = (d) => {
      if (!d) return;

      if (d.notificationSettings) {
        setSettingsForm(prev => ({ ...prev, ...d.notificationSettings }));
      }

      if (d.availability && typeof d.availability === "object" && !savingAvailRef.current) {
        setAvailabilityByDate(d.availability);
      }

      setProfileForm(prev => {
        const nextCut    = d.expertCutPercent ?? prev.expertCutPercent ?? 100;
        const nextFee    = d.consultationFee  ?? prev.consultationFee  ?? null;
        const nextStatus = d.approvalStatus   || prev.approvalStatus   || "pending";
        const nextVisible= d.isVisible        ?? prev.isVisible        ?? false;
        const nextBank   = d.bankEditStatus   || prev.bankEditStatus   || "locked";
        if (
          nextCut    === prev.expertCutPercent &&
          nextFee    === prev.consultationFee  &&
          nextStatus === prev.approvalStatus   &&
          nextVisible=== prev.isVisible        &&
          nextBank   === prev.bankEditStatus
        ) return prev;

        // Show contextual toast so expert sees the change immediately
        if (nextStatus !== prev.approvalStatus) {
          if (nextStatus === "approved") showToast("🎉 Your profile has been approved!");
          else if (nextStatus === "rejected") showToast("Your profile was not approved. Contact support.", "error");
        }
        if (nextBank !== prev.bankEditStatus && nextBank === "locked") {
          showToast("✅ Your bank details have been approved!");
        }
        if (nextFee !== prev.consultationFee && nextFee > 0 && nextVisible && !prev.isVisible) {
          showToast("🚀 You are now live! Clients can book sessions with you.");
        }

        return {
          ...prev,
          expertCutPercent: nextCut,
          consultationFee:  nextFee,
          approvalStatus:   nextStatus,
          isVisible:        nextVisible,
          bankEditStatus:   nextBank,
        };
      });
    };

    let onDemandTimer = null;

    // Watch the tiny signal doc — almost zero cost since it's one doc
    // that only changes when admin explicitly acts on this expert.
    const signalUnsub = onSnapshot(
      doc(db, "signals", `${expertId}_admin`),
      (signalSnap) => {
        const signalAt = signalSnap.data()?.at?.toDate?.();
        if (!signalAt) return;
        // Ignore stale signals on mount (older than 5 seconds)
        if (Date.now() - signalAt.getTime() > 5000) return;

        // Cancel any previous on-demand timer
        if (onDemandTimer) { clearTimeout(onDemandTimer); onDemandTimer = null; }

        // Open a live listener on the expert doc for exactly 1 second
        const expertUnsub = onSnapshot(
          doc(db, "experts", expertId),
          (docSnap) => {
            if (docSnap.exists()) applyExpertDoc(docSnap.data());
          }
        );

        onDemandTimer = setTimeout(() => {
          expertUnsub();
          onDemandTimer = null;
        }, 1000);
      },
      (err) => console.error("Admin signal listener error:", err)
    );

    return () => {
      signalUnsub();
      if (onDemandTimer) clearTimeout(onDemandTimer);
    };
  }, [expertId]);

  // ── REVIEWS — load once, not a live listener ─────────────────────────────
  // Reviews arrive rarely — only when a user completes a session AND submits
  // a rating. An always-on onSnapshot watching for that is a persistent read
  // that fires on every Firestore delivery for nothing. getDocs on mount is
  // correct here; refreshReviews() is called when the expert opens the
  // Reviews tab or when a booking flips to completed.
  //
  // reviewsLoadedRef tracks whether we've already fetched this session so
  // renderTabContent (a plain function called on every re-render) can't
  // fire multiple rapid getDocs calls while the expert is on the Reviews tab.
  const reviewsLoadedRef = React.useRef(false);

  const refreshReviews = React.useCallback(async (force = false) => {
    if (!force && reviewsLoadedRef.current) return; // already loaded — skip
    const absoluteExpertId = auth.currentUser?.uid || user?.uid || user?.id;
    if (!absoluteExpertId) { setLoadingReviews(false); return; }
    reviewsLoadedRef.current = true; // mark immediately to block concurrent calls
    try {
      const q = query(
        collection(db, "reviews"),
        where("expertId", "==", absoluteExpertId),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      const liveReviews = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setReviews(liveReviews);
      if (liveReviews.length > 0) {
        const score = liveReviews.reduce((sum, r) => sum + (r.rating ? Number(r.rating) : 0), 0);
        setStats(prev => ({ ...prev, rating: score / liveReviews.length, reviewsCount: liveReviews.length }));
      } else {
        setStats(prev => ({ ...prev, rating: 0, reviewsCount: 0 }));
      }
    } catch (err) {
      console.error("Reviews load failure:", err);
      reviewsLoadedRef.current = false; // allow retry on error
    } finally {
      setLoadingReviews(false);
    }
  }, [user?.uid, user?.id]);

  useEffect(() => {
    refreshReviews();
  }, [refreshReviews]);

  // Called when expert opens the Reviews tab — resets the loaded flag so
  // renderTabContent gets one fresh fetch per tab visit, but the ref guard
  // inside refreshReviews still blocks repeated calls from re-renders.
  const resetAndRefreshReviews = React.useCallback(() => {
    reviewsLoadedRef.current = false;
    refreshReviews();
  }, [refreshReviews]);

  // ── BOOKINGS — getDocs on mount + on-demand signal listener ────────────
  // Replaces the permanent onSnapshot on bookings where expertId.
  //
  // The permanent listener was firing on every booking field change across
  // ALL this expert's bookings — status, rescheduledAt, chatStarted, etc.
  // Now: getDocs once on mount, then watch signals/bookings_{expertId}.
  // Backend /api/bookings/finalize writes that signal after confirming a
  // new booking — so new bookings appear on the expert's dashboard within
  // 1 second of payment confirmation, with zero permanent listener cost.
  const fetchBookings = React.useCallback(async () => {
    if (!expertId) return;
    try {
      const snap = await getDocs(
        query(collection(db, "bookings"), where("expertId", "==", expertId))
      );
      setRawBookings(snap.docs.map(d => ({ docId: d.id, ...d.data() })));
    } catch (err) {
      console.error("Failed to load bookings:", err);
    }
  }, [expertId]);

  // Initial load on mount
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Signal watcher — backend writes signals/bookings_{expertId} after
  // /api/bookings/finalize confirms a new booking for this expert.
  useEffect(() => {
    if (!expertId) return;
    let onDemandTimer = null;

    const signalUnsub = onSnapshot(
      doc(db, "signals", `bookings_${expertId}`),
      (signalSnap) => {
        const signalAt = signalSnap.data()?.at?.toDate?.();
        if (!signalAt) return;
        // Ignore stale signals on mount
        if (Date.now() - signalAt.getTime() > 5000) return;

        if (onDemandTimer) { clearTimeout(onDemandTimer); onDemandTimer = null; }

        // Open bookings listener for 1 second to catch the new booking
        const bookingUnsub = onSnapshot(
          query(collection(db, "bookings"), where("expertId", "==", expertId)),
          (snap) => {
            setRawBookings(snap.docs.map(d => ({ docId: d.id, ...d.data() })));
          }
        );

        onDemandTimer = setTimeout(() => {
          bookingUnsub();
          onDemandTimer = null;
          showToast("📅 New booking received!");
        }, 1000);
      },
      (err) => console.error("Booking signal listener error:", err)
    );

    return () => {
      signalUnsub();
      if (onDemandTimer) clearTimeout(onDemandTimer);
    };
  // FIX: fetchBookings removed from deps — the signal watcher doesn't
  // call fetchBookings directly (it opens its own onSnapshot). Including
  // fetchBookings caused unnecessary re-registration of the signal listener.
  }, [expertId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The booking document itself doesn't carry the client's real name/company —
  // that lives on their own profile at users/{uid}. Whenever a booking
  // references a client id we haven't looked up yet, fetch that profile once
  // and cache it here so scheduledCalls can show the real name instead of
  // falling back to a generic "Client" label.
  useEffect(() => {
    const candidateIdFor = (b) => b.userId || b.clientId || b.uid || b.clientUid || b.customerId || null;
    const idsToFetch = Array.from(new Set(
      rawBookings.map(candidateIdFor).filter((id) => id && !(id in clientProfiles))
    ));
    if (idsToFetch.length === 0) return;

    let cancelled = false;
    (async () => {
      const entries = await Promise.all(idsToFetch.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, "users", id));
          return [id, snap.exists() ? snap.data() : null];
        } catch (err) {
          console.error("Failed to fetch client profile:", id, err);
          return [id, null];
        }
      }));
      if (cancelled) return;
      setClientProfiles((prev) => {
        const next = { ...prev };
        entries.forEach(([id, data]) => { next[id] = data; });
        return next;
      });
    })();

    return () => { cancelled = true; };
  // FIX: clientProfiles removed from deps — read via closure.
  // Including it caused a re-run on every setClientProfiles call (new object
  // reference each time), which re-ran the effect even when idsToFetch was
  // empty. rawBookings alone is the correct trigger.
  }, [rawBookings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist which notifications the expert has already opened/marked as read,
  // so the read state survives refreshes even though the notification feed
  // itself is regenerated from live booking data (not stored as documents).
  useEffect(() => {
    try {
      const storageKey = `sorted_read_notifications_${user?.uid || user?.id || "guest"}`;
      localStorage.setItem(storageKey, JSON.stringify([...readNotificationIds]));
    } catch (err) {
      console.error("Failed to persist read notifications:", err);
    }
  }, [readNotificationIds, user]);

  // Builds the notification feed from live booking data. Generates one
  // status notification per booking (new request / meeting live / payment /
  // cancelled) plus a separate "starting soon" reminder that appears once a
  // session is within 15 minutes of its start time. Re-runs on the 30s clock
  // tick so the 15-minute reminder appears/disappears at the right moment
  // without needing a page reload.
  useEffect(() => {
    if (!scheduledCalls || scheduledCalls.length === 0) {
      setNotifications([]); setUnreadCount(0); return;
    }
    const now = Date.now();
    const generated = [];

    scheduledCalls.forEach((call) => {
      const slotDate = parseSlotDateTime(call.rawDate, call.rawTime);

      // ---- 1) Status notification: booking / meeting live / payment / cancelled ----
      let title = "New Booking Request";
      let message = `${call.clientName} from ${call.clientCompany} booked a ${call.sessionType || "session"} for ${call.date}${call.time ? `, ${call.time}` : ""}.`;
      let type = "booking";
      let statusTimestamp = slotDate ? slotDate.getTime() : now;

      if (call.status === "live") {
        title = "Meeting is Live Now";
        message = `Your consultation with ${call.clientName} has started. Join now.`;
        type = "meeting";
      } else if (call.status === "completed") {
        title = "Payment Received";
        message = `₹${Math.round(call.payout || 0).toLocaleString("en-IN")} has been credited for your session with ${call.clientName}.`;
        type = "payment";
      } else if (call.status === "cancelled") {
        title = "Session Cancelled";
        message = `The session with ${call.clientName} was cancelled.`;
        type = "booking";
      }

      generated.push({
        id: `${call.id}-status`,
        bookingId: call.bookingId,
        clientName: call.clientName,
        type, title, message,
        time: call.status === "completed" ? (call.date || "") : (call.countdownText || "Scheduled"),
        timestamp: statusTimestamp
      });

      // ---- 2) 15-minutes-prior meeting reminder (only while still upcoming) ----
      if (call.status === "upcoming" && slotDate && settingsForm.sessionReminders) {
        const minsToStart = (slotDate.getTime() - now) / 60000;
        if (minsToStart <= 15 && minsToStart > -5) {
          generated.push({
            id: `${call.id}-reminder15`,
            bookingId: call.bookingId,
            clientName: call.clientName,
            type: "reminder",
            title: "Meeting Starting Soon",
            message: `Your meeting with ${call.clientName} ${minsToStart <= 0 ? "is starting now" : `starts in ${Math.ceil(minsToStart)} min`}. Make sure you're ready to join.`,
            time: minsToStart <= 0 ? "Starting now" : `in ${Math.ceil(minsToStart)}m`,
            timestamp: slotDate.getTime() - 15 * 60000
          });
        }
      }
    });

    generated.sort((a, b) => b.timestamp - a.timestamp);

    const withReadState = generated.map(n => ({ ...n, unread: !readNotificationIds.has(n.id) }));
    setNotifications(withReadState);
    setUnreadCount(withReadState.filter(n => n.unread).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledCalls, settingsForm.sessionReminders, readNotificationIds, tick]);

  useEffect(() => {
    const interval = setInterval(() => forceTick(t => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  // ---- # Notifications: mark-as-read / detail view handlers ----
  const markNotificationRead = (id) => {
    setReadNotificationIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const markAllNotificationsRead = () => {
    setReadNotificationIds(prev => {
      const next = new Set(prev);
      notifications.forEach(n => next.add(n.id));
      return next;
    });
  };

  const handleNotificationClick = (n) => {
    markNotificationRead(n.id);
    setSelectedNotification(n);
  };

  const closeNotificationDetail = () => setSelectedNotification(null);

  // Stable primitive extracted from profileForm — see comment near stats
  // useEffect below for why this must be a number, not profileForm itself.
  const expertCutPercent = profileForm.expertCutPercent ?? 100;

  useEffect(() => {
    const now = Date.now();
    // % of the session fee the expert actually earns (set by admin). Defaults
    // to 100% so nothing changes for experts who haven't had a cut configured.
    const cutFraction = (Number(expertCutPercent) || 100) / 100;
    const mapped = rawBookings.map((b) => {
      const slotDate = parseSlotDateTime(b.date, b.time);
      const durationMins = parseInt(b.duration) || SLOT_MINUTES;
      const slotEnd = slotDate ? new Date(slotDate.getTime() + durationMins * 60000) : null;

      let uiStatus = "upcoming";
      if (b.status === "cancelled") uiStatus = "cancelled";
      else if (b.status === "completed") uiStatus = "completed";
      else if (slotDate && slotEnd) {
        if (now >= slotDate.getTime() && now <= slotEnd.getTime()) uiStatus = "live";
        else if (now > slotEnd.getTime()) uiStatus = "completed";
      }

      // b.price is the full amount the client paid (same figure shown on the
      // user side). Everywhere this dashboard displays money to the expert,
      // it should reflect only their cut of that amount.
      const rawPrice = b.price || 0;
      const expertShare = rawPrice * cutFraction;

      const clientId = b.userId || b.clientId || b.uid || b.clientUid || b.customerId || null;
      const clientProfile = clientId ? clientProfiles[clientId] : null;

      return {
        id: b.docId,
        bookingId: b.docId,
        clientName: clientProfile?.fullName || clientProfile?.name || b.clientName || b.userName || b.userDisplayName || b.name || b.fullName || (b.firstName ? `${b.firstName} ${b.lastName || ""}`.trim() : "") || "Client",
        clientCompany: clientProfile?.company || b.startupName || b.companyName || b.userEmail || "Independent",
        topic: b.topic || b.query || b.about || "General Consultation",
        date: b.date,
        time: b.time || "",
        duration: b.duration || `${SLOT_MINUTES} min`,
        payout: expertShare,
        status: uiStatus,
        countdownText: slotDate ? formatTimeUntil(slotDate) : "",
        sessionType: b.sessionType || "Chat",
        meetLink: b.expertMeetLink || b.meetLink || null, // expertMeetLink has expert's name pre-filled
        rawDate: b.date,
        rawTime: b.time,
        price: expertShare,
        fullSessionPrice: rawPrice, // kept for reference; not the expert's take-home amount
        rating: b.rating || null
      };
    });

    mapped.sort((a, b) => {
      const da = parseSlotDateTime(a.rawDate, a.rawTime);
      const db_ = parseSlotDateTime(b.rawDate, b.rawTime);
      if (!da || !db_) return 0;
      return da - db_;
    });
    setScheduledCalls(mapped);
  }, [rawBookings, expertCutPercent, clientProfiles]);

  // ── Stable primitive extracted from profileForm so scheduledCalls useEffect
  // does NOT re-run just because setProfileForm created a new object reference
  // (which happens on every onSnapshot delivery from experts/{id}).
  // Previously: dep was profileForm.expertCutPercent — since setProfileForm
  // always spreads into a new object, React saw a new reference every snapshot,
  // re-ran scheduledCalls, changed completedCount, triggered stats fetch,
  // which wrote statsUpdatedAt to experts/{id}, which fired the snapshot again.
  // That loop ran at Firestore round-trip speed (~200-500ms), not 30s.
  // Now: dep is a stable number — only changes when the actual value changes.
  useEffect(() => {
    const completed = scheduledCalls.filter(c => c.status === "completed");
    const totalEarnings = completed.reduce((sum, c) => sum + (Number(c.payout) || 0), 0);

    const now = new Date();
    const thisMonthCompleted = completed.filter(c => {
      const d = parseSlotDateTime(c.rawDate, c.rawTime) || new Date(c.rawDate);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    const monthEarnings = thisMonthCompleted.reduce((sum, c) => sum + (Number(c.payout) || 0), 0);

    setStats(prev => ({
      ...prev,
      totalEarnings,
      monthEarnings,
      totalSessions: completed.length,
      thisMonthSessions: thisMonthCompleted.length
      // pendingPayout intentionally left untouched here — only the server
      // (which knows about real payouts/{expertId} records) can compute it.
    }));
  }, [scheduledCalls]);

  // Authoritative stats — computed server-side from real booking price +
  // admin-set expertCutPercent (never trusted from the browser), and from
  // the real `payouts` ledger for pendingPayout. This also persists the
  // result onto experts/{expertId}.stats so other views (an admin panel,
  // a future report) don't need to re-derive it from scratch. Re-fetches
  // whenever the set of completed sessions changes, so a newly-completed
  // call or a newly-recorded payout is reflected without a manual refresh.
  const completedCount = scheduledCalls.filter(c => c.status === "completed").length;
  // FIX: ref guard — completedCount is recomputed on every render, but its
  // numeric VALUE only changes when a booking actually completes. Without this
  // guard, any re-render (e.g. from onSnapshot delivering the same experts doc)
  // would re-run the effect and fire another stats fetch → Firestore write →
  // onSnapshot → re-render loop running at sub-second speed.
  const lastCompletedCountRef = useRef(-1);
  useEffect(() => {
    if (completedCount === lastCompletedCountRef.current) return;
    lastCompletedCountRef.current = completedCount;

    const expertId = user?.uid || user?.id;
    if (!expertId || !auth.currentUser) return;

    let cancelled = false;
    (async () => {
      try {
        const idToken = await auth.currentUser.getIdToken();
        const res = await fetch(`${API_BASE_URL}/api/experts/stats`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        const data = await res.json();
        if (!cancelled && res.ok && data.success) {
          setStats(prev => ({ ...prev, ...data.stats }));
        }
      } catch (err) {
        // Non-fatal — the local estimate above still covers earnings/session
        // counts; only pendingPayout truly depends on this call succeeding.
        console.error("Failed to fetch server-side expert stats:", err);
      }
    })();

    return () => { cancelled = true; };
  }, [user, completedCount]);

  // Closes out the session doc ExpertLogin.js opened at sign-in
  // (experts/{expertId}/sessions/{sessionId}, id stashed in sessionStorage
  // under "sorted_expert_session_id"). durationSeconds is NOT computed here
  // — this just asks the server to close it, and /api/experts/session-close
  // derives durationSeconds from its own stored `startedAt`, since the
  // Firestore rules for that subcollection don't allow the client to write
  // `durationSeconds`/`endedAt` directly (same trust boundary as
  // /api/experts/stats). Safe to call more than once — the endpoint is
  // idempotent if the session's already closed.
  const closeCurrentSession = async () => {
    const sessionId = sessionStorage.getItem("sorted_expert_session_id");
    if (!sessionId || !auth.currentUser) return;
    try {
      const idToken = await auth.currentUser.getIdToken();
      await fetch(`${API_BASE_URL}/api/experts/session-close`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ sessionId }),
      });
    } catch (err) {
      // Non-fatal — a missed close just means this one session's duration
      // never gets recorded; it should never block the actual logout/
      // delete/deactivate action it's attached to.
      console.error("Failed to close session:", err);
    } finally {
      sessionStorage.removeItem("sorted_expert_session_id");
    }
  };

  // Best-effort coverage for tab-close / browser-close, which never runs
  // the explicit logout handler below. `beforeunload` handlers can't reliably
  // await a promise, so this fires the request with `keepalive: true`
  // (lets the browser complete an in-flight fetch after the page starts
  // unloading) rather than `navigator.sendBeacon` — sendBeacon can't attach
  // a custom Authorization header, which this endpoint requires.
  useEffect(() => {
    if (!expertId) return;

    const handleUnload = () => {
      const sessionId = sessionStorage.getItem("sorted_expert_session_id");
      if (!sessionId || !auth.currentUser) return;
      auth.currentUser.getIdToken().then((idToken) => {
        fetch(`${API_BASE_URL}/api/experts/session-close`, {
          method: "POST",
          keepalive: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ sessionId }),
        }).catch(() => {});
      }).catch(() => {});
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [expertId]);

  // Wraps onLogout so the session gets closed BEFORE navigating away —
  // used by both logout buttons below instead of calling onLogout directly.
  const handleLogoutClick = async () => {
    await closeCurrentSession();
    if (onLogout) onLogout();
  };

  const handleSaveAllSettings = async () => {
    const expertId = user?.uid || user?.id;
    if (!expertId) return;
    setIsSavingSettings(true);
    try {
      await updateDoc(doc(db, "experts", expertId), {
        notificationSettings: settingsForm,
        // lastUpdatedSettings removed — it was a noisy write that triggered
        // the experts doc snapshot for every browsing user on TalkToExperts.
        // notificationSettings is the field that matters here.
      });
      showToast("Preferences saved successfully.");
    } catch (error) {
      showToast(`Failed to save preferences: ${error.message}`, "error");
    } finally {
      setIsSavingSettings(false);
    }
  };

const handleDeleteAccount = async (stepOverride) => {
    const expertId = user?.uid || user?.id;
    if (!expertId) return;

    // First click on the sidebar/settings delete button
    if (deleteConfirmStep === 0 && stepOverride === undefined) {
      setDeleteConfirmStep(1);
      return;
    }

    // When clicking "Next/Proceed" from the first screen
    if (stepOverride === 2) {
      setDeleteConfirmStep(2);
      return;
    }

    // When clicking "Yes" from the final confirmation screen
    if (stepOverride === "FINAL_DELETE") {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("No authenticated user session found. Please re-login and try again.");

        // FIX (Issue 3): Previously called currentUser.delete() directly which:
        //   (a) requires recent login and fails silently with requires-recent-login
        //   (b) left the Firestore doc marked "deleted" even when Auth deletion failed
        //   (c) had no rollback if the Auth delete threw after the Firestore write
        //
        // New flow:
        //   1. Mark Firestore first (status: "deleted")
        //   2. Close the session doc
        //   3. Call backend /api/admin/delete-expert-auth (Admin SDK — no recency requirement)
        //   4. If step 3 fails, roll back the Firestore doc so the expert can retry
        //      after re-logging in without being locked out of their own account

        await updateDoc(doc(db, "experts", expertId), {
          approvalStatus: "deleted",
          isVisible:      false,
          deletedAt:      serverTimestamp(),
        });

        await closeCurrentSession();

        try {
          const idToken = await currentUser.getIdToken();
          const res = await fetch(`${API_BASE_URL}/api/admin/delete-expert-auth`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ expertId }),
          });
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.error || "Auth deletion failed.");
          }
        } catch (authErr) {
          // Auth deletion failed — roll back the Firestore change so the
          // expert isn't locked out of their own portal while Auth still exists.
          await updateDoc(doc(db, "experts", expertId), {
            approvalStatus: "approved",
            isVisible:      true,
            deletedAt:      null,
          }).catch(() => {});
          throw authErr; // bubble up to outer catch for toast
        }

        showToast("Account closed. Your credentials have been cleared successfully.");
        setDeleteConfirmStep(0);
        if (onLogout) {
          onLogout();
        } else {
          await auth.signOut();
          window.location.reload();
        }
      } catch (error) {
        console.error("Deletion error:", error);
        showToast(`Failed to close account: ${error.message}`, "error");
        setDeleteConfirmStep(0);
      }
    }
  };

  // Opens the in-app confirmation modal instead of the native browser confirm() pop-up.
  const handleDeactivateAccount = () => setDeactivateConfirmOpen(true);

  const confirmDeactivateAccount = async () => {
    const expertId = user?.uid || user?.id;
    setDeactivateConfirmOpen(false);
    if (!expertId) return;
    try {
      await updateDoc(doc(db, "experts", expertId), {
        isVisible: false,
        approvalStatus: "deactivated",
        deactivatedAt: serverTimestamp(),
        bankDetailsAdded: false,
        bankEditStatus: "locked",
        bankHolder: "",
        bankName: "",
        bankBranch: "",
        bankLocation: "",
        bankIfsc: "",
        bankAccountLast4: "",
        bankDocUrl: "",
        bankDocName: ""
      });

      showToast("Account deactivated and banking details cleared. Logging out...");
      await closeCurrentSession();
      setTimeout(async () => {
        if (onLogout) {
          onLogout();
        } else {
          await auth.signOut();
          window.location.reload();
        }
      }, 1500);
    } catch (error) {
      showToast(`Failed to deactivate account: ${error.message}`, "error");
    }
  };

  const handleSaveBankDetails = async () => {
    setBankErrorMsg(""); setBankSuccessMsg("");
    if (bankForm.accountNum !== bankForm.confirmAccountNum) {
      setBankErrorMsg("Account numbers do not match. Please re-enter and try again.");
      return;
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankForm.ifsc.toUpperCase())) {
      setBankErrorMsg("Invalid IFSC format. Please check and try again.");
      return;
    }
    if (!bankDocFile && !bankForm.docUrl) {
      setBankErrorMsg("Please upload your passbook, a cancelled cheque, or any document showing your account number and name.");
      return;
    }
    const expertId = user?.uid || user?.id;
    if (!expertId) return;
    try {
      let docUrl = bankForm.docUrl;
      let docName = bankForm.docName;

      if (bankDocFile) {
        setBankDocUploading(true);

        // 1. Ask the backend for a short-lived presigned S3 upload URL. The
        //    backend now derives the storage path from the caller's verified
        //    Firebase ID token rather than trusting a userId in the body.
        const idToken = await auth.currentUser.getIdToken();
        const presignRes = await fetch(`${API_BASE_URL}/api/experts/generate-presigned-url`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            fileName: bankDocFile.name,
            fileType: bankDocFile.type,
            folderPath: "bankDocuments"
          })
        });
        const presignData = await presignRes.json();
        if (!presignRes.ok || !presignData.success) {
          throw new Error(presignData.error || "Could not get an upload authorization from the server.");
        }

        // 2. Upload the file straight to S3 using the presigned URL
        const uploadRes = await fetch(presignData.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": bankDocFile.type },
          body: bankDocFile
        });
        if (!uploadRes.ok) {
          throw new Error("File upload to storage failed. Please try again.");
        }

        docUrl = presignData.publicUrl;
        docName = bankDocFile.name;
        setBankDocUploading(false);
      }

      await updateDoc(doc(db, "experts", expertId), {
        bankEditStatus: "pending_approval",
        bankDetailsAdded: true,
        bankHolder: bankForm.holder,
        bankName: bankForm.bankName,
        bankBranch: bankForm.branch,
        bankLocation: bankForm.location,
        bankIfsc: bankForm.ifsc.toUpperCase(),
        bankAccountLast4: bankForm.accountNum.slice(-4),
        bankDocUrl: docUrl,
        bankDocName: docName,
        bankUpdatedAt: serverTimestamp(),
        // isVisible is intentionally NOT written here. Firestore rules block
        // any self-write from the expert that touches isVisible (it's
        // admin-only, set only via the admin's "Set & Publish" fee/cut
        // assignment) — including this update trying to set it, even to
        // false. If an already-visible expert resubmits bank details and you
        // want them automatically unpublished until re-approved, that flip
        // needs to happen through a privileged backend endpoint (Firebase
        // Admin), the same pattern already used for /api/reviews — not a
        // direct client write.
      });

      await addDoc(collection(db, "adminRequests"), {
        type: "bank_approval",
        expertId,
        expertName: profileForm.name || "",
        expertEmail: profileForm.email || user?.email || "",
        bankHolder: bankForm.holder,
        bankName: bankForm.bankName,
        bankBranch: bankForm.branch,
        bankLocation: bankForm.location,
        bankIfsc: bankForm.ifsc.toUpperCase(),
        bankAccountLast4: bankForm.accountNum.slice(-4),
        bankDocUrl: docUrl,
        bankDocName: docName,
        status: "pending",
        createdAt: serverTimestamp()
      });

      // Firestore rules block us from touching isVisible directly — it's
      // admin-only. If this expert was already live and is now resubmitting
      // (post-edit) bank details, tell the backend to revoke visibility via
      // the Admin SDK until the admin re-approves. The admin's "Approve Bank"
      // action restores it automatically afterwards if the rate's already
      // set. Non-fatal if this call fails — the bank details themselves
      // already saved successfully above, so don't block on it.
      try {
        const idTokenForRevoke = await auth.currentUser.getIdToken();
        await fetch(`${API_BASE_URL}/api/experts/bank-resubmitted`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idTokenForRevoke}`,
          },
        });
      } catch (revokeErr) {
        console.error("Failed to sync visibility state after bank resubmission:", revokeErr.message);
      }

      setBankForm(prev => ({ ...prev, docUrl, docName }));
      setBankDocFile(null);
      setIsEditingBank(false);
      setBankSuccessMsg("Bank details saved and sent to admin for approval. Once approved, you'll be visible to clients again.");
    } catch (err) {
      setBankDocUploading(false);
      setBankErrorMsg("Failed to save bank details: " + err.message);
    }
  };

  const handleRequestBankUnlock = async () => {
    const expertId = user?.uid || user?.id;
    if (!expertId) return;
    if (!bankForm.holder) { setIsEditingBank(true); return; }
    try {
      await updateDoc(doc(db, "experts", expertId), { bankEditStatus: "requested", bankRequestTimestamp: serverTimestamp() });
      setBankRequestSent(true);
      showToast("Request sent. Please wait for the admin to unlock your banking details.");
    } catch (error) {
      showToast(`Failed to submit unlock request: ${error.message}`, "error");
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const expertId = user?.uid || user?.id;
    if (!expertId) return;
    try {
      await updateDoc(doc(db, "experts", expertId), {
        // FIX (Issue 8): split on /\s+/ to handle multiple or leading spaces
        firstName: profileForm.name.trim().split(/\s+/)[0] || "",
        lastName: profileForm.name.trim().split(/\s+/).slice(1).join(" ") || "",
        displayName: profileForm.name,
        company: profileForm.company,
        location: profileForm.location,
        bio: profileForm.bio,
        phone: profileForm.phone,
        industries: profileForm.industry,
        industry: profileForm.industry.join(", "),
        // profileUpdatedAt removed — was triggering experts doc snapshot
        // on every profile save, firing to all TalkToExperts users.
      });
      setIsEditingProfile(false);
      setIsEditingBank(false);
      showToast("Profile updated successfully!");
    } catch (err) {
      showToast("Failed to save profile: " + err.message, "error");
    }
  };

  // Uploads a new profile photo straight away (independent of the "Save Changes" form
  // submit) so the picture updates immediately, the same way most apps handle avatars.
  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      showToast("Please upload a JPG, PNG, or WEBP image.", "error");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast("Image is too large. Please upload a file under 10MB.", "error");
      e.target.value = "";
      return;
    }
    const expertId = user?.uid || user?.id;
    if (!expertId) return;
    setProfilePhotoUploading(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const presignRes = await fetch(`${API_BASE_URL}/api/experts/generate-presigned-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          folderPath: "profilePhotos"
        })
      });
      const presignData = await presignRes.json();
      if (!presignRes.ok || !presignData.success) {
        throw new Error(presignData.error || "Could not get an upload authorization from the server.");
      }
      const uploadRes = await fetch(presignData.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file
      });
      if (!uploadRes.ok) throw new Error("Image upload to storage failed. Please try again.");

      const photoUrl = presignData.publicUrl;
      await updateDoc(doc(db, "experts", expertId), {
        profilePhoto: photoUrl,
        profilePic:   photoUrl,
        "documentsUrls.profilePhoto": photoUrl,
        // profileUpdatedAt removed — same reason as profile save above.
      });
      setProfileForm(prev => ({ ...prev, profilePhoto: photoUrl }));
      showToast("Profile photo updated!");
    } catch (err) {
      showToast("Failed to upload photo: " + err.message, "error");
    } finally {
      setProfilePhotoUploading(false);
      e.target.value = "";
    }
  };

  // Mirrors the Industry selector on the signup form: pick up to MAX_INDUSTRY_SELECTIONS.
  const handleIndustryToggle = (ind) => {
    setProfileForm(prev => {
      const current = prev.industry || [];
      if (current.includes(ind)) {
        return { ...prev, industry: current.filter(i => i !== ind) };
      }
      if (current.length >= MAX_INDUSTRY_SELECTIONS) {
        showToast(`You can select up to ${MAX_INDUSTRY_SELECTIONS} industries only.`, "error");
        return prev;
      }
      return { ...prev, industry: [...current, ind] };
    });
  };

  // Caps the bio at MAX_BIO_WORDS words and MAX_BIO_CHARS characters, whichever is hit first.
  const handleBioChange = (e) => {
    let value = e.target.value;
    if (value.length > MAX_BIO_CHARS) value = value.slice(0, MAX_BIO_CHARS);
    const words = value.split(/\s+/).filter(Boolean);
    if (words.length > MAX_BIO_WORDS) {
      value = words.slice(0, MAX_BIO_WORDS).join(" ");
    }
    setProfileForm(prev => ({ ...prev, bio: value }));
  };

  // Opens Edit Profile and, when the bank section is actually editable, opens it too —
  // so bank details are reachable from the single "Edit Profile" action.
  const handleOpenEditProfile = () => {
    setIsEditingProfile(true);
    if (!bankForm.holder || profileForm.bankEditStatus === "approved") {
      setIsEditingBank(true);
    }
  };

  const handleCancelEditProfile = () => {
    setIsEditingProfile(false);
    setIsEditingBank(false);
  };

  // Opens the Daily.co room in a new tab and fire-and-forgets a recording
  // trigger to the backend. The backend waits 8s before calling the Daily API
  // so the participant has time to connect. meetLink is the room URL;
  // call.id (the Firestore doc id) is used as the booking ref; the Daily
  // room name is stored as call.eventId on the booking doc.
  const handleJoinCall = async (call) => {
    if (!call.meetLink) {
      showToast("Meeting link is not ready yet.", "error");
      return;
    }
    window.open(call.meetLink, "_blank");

    if (call.eventId) {
      try {
        const idToken = await auth.currentUser.getIdToken();
        fetch(`${API_BASE_URL}/api/start-recording/${call.eventId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        }).catch(err => console.warn("start-recording fire-and-forget failed:", err.message));
      } catch (err) {
        console.warn("start-recording token error:", err.message);
      }
    }
  };

  const submitReschedule = async () => {
    if (!rescheduleData.date || !rescheduleData.time) {
      showToast("Please select both a valid date and time.", "error");
      return;
    }

    // Need the raw booking doc (not the display-mapped `scheduledCalls` entry)
    // to get at eventId / userEmail for the Daily API call below.
    const rawBooking = rawBookings.find((b) => b.docId === rescheduleData.bookingId);
    if (!rawBooking) {
      showToast("Couldn't find this booking. Please refresh and try again.", "error");
      return;
    }
    if (!rawBooking.eventId) {
      showToast("This booking has no video room on file — please contact support to reschedule.", "error");
      return;
    }

    try {
      const formattedTimeInput = rescheduleData.time; // "HH:MM", 24-hour, from <input type="time">
      let [h, m] = formattedTimeInput.split(":");
      h = parseInt(h);
      const meridian = h >= 12 ? "PM" : "AM";
      h = h % 12;
      if (h === 0) h = 12;
      const cleanTimeStr = `${h}:${m} ${meridian}`;

      // Build local (Asia/Kolkata) start/end datetimes for the calendar API.
      // No trailing "Z" — the backend pairs these with timeZone: "Asia/Kolkata".
      const pad = (n) => String(n).padStart(2, "0");
      const startTime = `${rescheduleData.date}T${formattedTimeInput}:00`;
      const durationMins = parseInt(rawBooking.duration) || SLOT_MINUTES;
      const startDateObj = new Date(startTime);
      const endDateObj = new Date(startDateObj.getTime() + durationMins * 60000);
      const endTime = `${endDateObj.getFullYear()}-${pad(endDateObj.getMonth() + 1)}-${pad(endDateObj.getDate())}T${pad(endDateObj.getHours())}:${pad(endDateObj.getMinutes())}:00`;

      // 1. Reschedule the Daily room first: extends the existing room's expiry
      //    (or creates a fresh room if it has already expired) and syncs
      //    eventId/meetLink on the Firestore booking doc server-side.
      const idToken = await auth.currentUser.getIdToken();
      const rescheduleRes = await fetch(`${API_BASE_URL}/api/update-meet/${rawBooking.eventId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ startTime }),
      });
      const rescheduleData_ = await rescheduleRes.json();
      if (!rescheduleRes.ok || !rescheduleData_.success) {
        throw new Error(rescheduleData_.error || "Failed to update the video call room.");
      }

      // 2. Only once the Daily side is confirmed do we sync the human-facing
      //    date/time on the Firestore booking. eventId/meetLink are already
      //    updated server-side by /api/update-meet.
      await updateDoc(doc(db, "bookings", rescheduleData.bookingId), {
        date: rescheduleData.date,
        time: cleanTimeStr,
        rescheduledByExpert: true
      });

      showToast("Meeting rescheduled — the new video link is ready and both sides have been notified.");
      setRescheduleData({ show: false, bookingId: null, date: "", time: "" });
    } catch (error) {
      console.error("Reschedule failed:", error);
      showToast(error.message || "Unable to save the reschedule. Please try again.", "error");
    }
  };

  // Opens the confirmation modal for cancelling an upcoming session.
  const handleCancelCall = (call) => setCancelCallData({ show: true, call });

  // FIX (Issue 2): Cancellation now goes through the backend /api/delete-meet
  // route instead of writing directly to Firestore. The backend:
  //   1. Cancels the Daily.co room so the user can't accidentally join
  //   2. Sends an in-app notification to the user that their session was cancelled
  //   3. Fires the booking.cancelled webhook for downstream integrations
  //   4. Writes the Firestore status update with the Admin SDK (privileged)
  // Previously this wrote status:"cancelled" directly from the browser — no
  // notification, no room teardown, no webhook, and the EmailJS public key
  // was exposed in the JS bundle (anyone could read it from DevTools and
  // send unlimited emails through the account up to the monthly limit).
  const confirmCancelCall = async () => {
    const call = cancelCallData.call;
    setCancelCallData({ show: false, call: null });
    if (!call) return;

    // Find the raw booking to get the eventId (Daily room name)
    const rawBooking = rawBookings.find((b) => b.docId === call.id);
    const eventId    = rawBooking?.eventId || call.id;

    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/delete-meet`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ eventId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Cancellation failed.");
      }
      showToast("Session cancelled — the client has been notified.");
      fetchBookings(); // refresh the booking list to reflect the new status
    } catch (error) {
      showToast(`Failed to cancel session: ${error.message}`, "error");
    }
  };

  const saveAvailability = async (nextState) => {
    setAvailabilityByDate(nextState); // optimistic local update
    if (!expertId) return;
    try {
      // FIX: set the flag BEFORE the write so the onSnapshot that fires
      // immediately after knows to skip its setAvailabilityByDate call,
      // preventing the write → snapshot → setState → re-render flicker loop.
      savingAvailRef.current = true;
      await updateDoc(doc(db, "experts", expertId), { availability: nextState });
      // Signal user portal (TalkToExperts) that expert slots changed.
      // One shared doc — always overwritten, never grows.
      setDoc(doc(db, "signals", "experts_updated"), {
        type: "experts_updated",
        at:   serverTimestamp(),
      }).catch(err => console.error("Failed to write slot signal:", err));
    } catch (error) {
      console.error("Failed to save availability:", error);
      // Revert optimistic update on failure
      setAvailabilityByDate(availabilityByDate);
    } finally {
      // FIX: reset after a short delay so the on-demand snapshot that fires
      // async after the updateDoc resolves still sees the flag as true and
      // skips its setAvailabilityByDate call — preventing a flicker where
      // the snapshot overwrites the optimistic local state before settling.
      // 500ms is well within the 1-second on-demand listener window.
      setTimeout(() => { savingAvailRef.current = false; }, 500);
    }
  };

  const getDayState = (dateKey) => availabilityByDate[dateKey] || { active: true, blocked: false, slots: [] };

  const handleToggleDate = (dateKey) => {
    const current = getDayState(dateKey);
    saveAvailability({ ...availabilityByDate, [dateKey]: { ...current, active: !current.active } });
  };

  const handleToggleBlocked = (dateKey) => {
    const current = getDayState(dateKey);
    saveAvailability({ ...availabilityByDate, [dateKey]: { ...current, blocked: !current.blocked } });
  };

  const formatSlotLabel = (startTime) => {
    const [h, m] = startTime.split(":").map(Number);
    let meridian = h >= 12 ? "PM" : "AM";
    let displayH = h % 12; if (displayH === 0) displayH = 12;
    const startLabel = `${displayH}:${String(m).padStart(2, "0")} ${meridian}`;
    const endLabel = addMinutesToTimeStr(startLabel, SLOT_MINUTES);
    return `${startLabel} – ${endLabel}`;
  };

  const generateTimeOptions = () => {
    const options = [];
    // 9:00 AM (9*60) to 9:00 PM (21*60), every SLOT_MINUTES, last start time leaves room for a full slot before 9 PM
    for (let mins = 9 * 60; mins <= 21 * 60 - SLOT_MINUTES; mins += SLOT_MINUTES) {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      let meridian = h >= 12 ? "PM" : "AM";
      let displayH = h % 12; if (displayH === 0) displayH = 12;
      const shortLabel = `${displayH}:${String(m).padStart(2, "0")} ${meridian}`;
      options.push({ value, label: formatSlotLabel(value), shortLabel });
    }
    return options;
  };

  const handleAddSlot = (dateKey, startTime) => {
    if (!startTime) return;
    const slotLabel = formatSlotLabel(startTime);
    const current = getDayState(dateKey);
    saveAvailability({
      ...availabilityByDate,
      [dateKey]: { ...current, active: true, slots: [...(current.slots || []), slotLabel].sort((a, b) => slotSortKey(a) - slotSortKey(b)) }
    });
  };

  const handleAddMultipleSlots = (dateKey, startTimes) => {
    if (!startTimes || !startTimes.length) return;
    const current = getDayState(dateKey);
    const newLabels = startTimes.map(formatSlotLabel);
    const merged = Array.from(new Set([...(current.slots || []), ...newLabels])).sort((a, b) => slotSortKey(a) - slotSortKey(b));
    saveAvailability({
      ...availabilityByDate,
      [dateKey]: { ...current, active: true, slots: merged }
    });
  };

  const handleRemoveSlot = (dateKey, idx) => {
    const current = getDayState(dateKey);
    saveAvailability({
      ...availabilityByDate,
      [dateKey]: { ...current, slots: current.slots.filter((_, i) => i !== idx) }
    });
  };

  return (
    <div className="dashboard-container">
      {toast && (
        <div className={`app-toast-wrap ${toast.type === "error" ? "app-toast-error" : "app-toast-success"}`}>
          <span className="app-toast-icon">{toast.type === "error" ? "⚠️" : "✓"}</span>
          <span className="app-toast-msg">{toast.message}</span>
          <button className="app-toast-close" onClick={() => setToast(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {deactivateConfirmOpen && (
        <div className="reschedule-modal-overlay" onClick={() => setDeactivateConfirmOpen(false)}>
          <div className="reschedule-modal-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="reschedule-modal-title">Deactivate Profile?</h3>
            <p className="reschedule-modal-subtitle">This will hide your profile from clients, log you out, and clear your bank details for security. You'll need to re-verify them next time you sign in.</p>
            <div className="reschedule-modal-actions">
              <button onClick={() => setDeactivateConfirmOpen(false)} className="reschedule-cancel-btn">Cancel</button>
              <button onClick={confirmDeactivateAccount} className="reschedule-confirm-btn">Yes, Deactivate</button>
            </div>
          </div>
        </div>
      )}

      {cancelCallData.show && (
        <div className="reschedule-modal-overlay" onClick={() => setCancelCallData({ show: false, call: null })}>
          <div className="reschedule-modal-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="reschedule-modal-title" style={{ color: "#ef4444" }}>Cancel this session?</h3>
            <p className="reschedule-modal-subtitle">
              This will cancel your session with {cancelCallData.call?.clientName || "the client"} on {cancelCallData.call?.date || "—"} at {cancelCallData.call?.time || "—"}. This can't be undone, and the admin will be notified.
            </p>
            <div className="reschedule-modal-actions">
              <button onClick={() => setCancelCallData({ show: false, call: null })} className="reschedule-cancel-btn">Keep Session</button>
              <button onClick={confirmCancelCall} className="reschedule-confirm-btn" style={{ backgroundColor: "#ef4444", borderColor: "#ef4444", color: "#fff" }}>Yes, Cancel Session</button>
            </div>
          </div>
        </div>
      )}

      {rescheduleData.show && (
        <div className="reschedule-modal-overlay">
          <div className="reschedule-modal-card">
            <h3 className="reschedule-modal-title">Reschedule Session</h3>
            <p className="reschedule-modal-subtitle">Pick a new date and time for this consultation.</p>
            <div className="reschedule-modal-fields">
              <div className="reschedule-field-item">
                <label className="reschedule-field-label">Select Date</label>
                <input type="date" value={rescheduleData.date} onChange={(e) => setRescheduleData({ ...rescheduleData, date: e.target.value })} className="reschedule-input-ctrl" />
              </div>
              <div className="reschedule-field-item">
                <label className="reschedule-field-label">Select Time</label>
                <input type="time" value={rescheduleData.time} onChange={(e) => setRescheduleData({ ...rescheduleData, time: e.target.value })} className="reschedule-input-ctrl" />
              </div>
            </div>
            <div className="reschedule-modal-actions">
              <button onClick={() => setRescheduleData({ show: false, bookingId: null, date: "", time: "" })} className="reschedule-cancel-btn">Cancel</button>
              <button onClick={submitReschedule} className="reschedule-confirm-btn">Confirm Reschedule</button>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================
          MOBILE HEADER — hamburger menu, SORTED wordmark, notification
          bell, and avatar. Only visible below the mobile breakpoint;
          the desktop sidebar + content-header handle wider screens.
          ================================================================ */}
      <header className="mobile-top-header">
        <button
          className="mobile-hamburger-btn"
          onClick={() => setMobileDrawerOpen(true)}
          aria-label="Open menu"
        >
          <span></span><span></span><span></span>
        </button>
        <div className="mobile-header-brand">
          <span
            className="mobile-header-brand-inner clickable-brand"
            onClick={() => setActiveTabMobile("dashboard")}
            role="button"
            tabIndex={0}
            title="Go to Dashboard"
          >
            <span className="mobile-header-badge"><FiCheckCircle /></span>
            <span className="mobile-header-wordmark">SORTED</span>
          </span>
        </div>
        <div className="mobile-header-actions">
          <button className="mobile-header-bell-btn" onClick={() => setActiveTabMobile("notifications")} aria-label="Notifications">
            <FiBell /> {unreadCount > 0 && <span className="dot"></span>}
          </button>
          <div
            className="mobile-header-avatar"
            style={{ background: avatarColorOf(profileForm.name) }}
            onClick={() => setActiveTabMobile("profile")}
            role="button"
            tabIndex={0}
            title="Go to My Profile"
          >
            {profileForm.profilePhoto && !photoLoadError ? (
              <img src={profileForm.profilePhoto} alt={profileForm.name} onError={() => setPhotoLoadError(true)} />
            ) : (profileForm.name ? initialsOf(profileForm.name) : "E")}
          </div>
        </div>
      </header>

      {/* Backdrop shown behind the sliding drawer / "More" sheet on mobile */}
      {(mobileDrawerOpen || mobileMoreOpen) && (
        <div
          className="mobile-overlay-backdrop"
          onClick={() => { setMobileDrawerOpen(false); setMobileMoreOpen(false); }}
        ></div>
      )}

      <aside className={`sidebar ${mobileDrawerOpen ? "mobile-drawer-open" : ""}`}>
        <div
          className="logo-section clickable-brand"
          onClick={() => setActiveTab("dashboard")}
          role="button"
          tabIndex={0}
          title="Go to Dashboard"
        >
          <img src={logoImg} alt="SORTED Logo" className="logo-img-asset" />
          <div className="logo-title-area">
            <h2>SORTED</h2>
            <span className="portal-tag">Expert Portal</span>
          </div>
        </div>

        <div
          className={`profile-card clickable-profile-card ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => setActiveTab("profile")}
          role="button"
          tabIndex={0}
          title="Go to My Profile"
        >
          <div className="avatar-placeholder" style={{ background: avatarColorOf(profileForm.name) }}>
            {profileForm.profilePhoto && !photoLoadError ? (
              <img src={profileForm.profilePhoto} alt={profileForm.name} className="avatar-photo-img" onError={() => { console.warn("Profile photo failed to load:", profileForm.profilePhoto); setPhotoLoadError(true); }} />
            ) : (profileForm.name ? initialsOf(profileForm.name) : "E")}
          </div>
          <div className="profile-info">
            <h3>{profileForm.name || "Expert"}</h3>
            <p>{profileForm.company || "Independent Consultant"}</p>
          </div>
        </div>

        {/* ================================================================
            SIDEBAR NAVIGATION — one button per section below.
            Order matches the sections in renderTabContent() further down.
            ================================================================ */}
        <button className="mobile-drawer-close-btn" onClick={() => setMobileDrawerOpen(false)} aria-label="Close menu">×</button>

        <nav className="nav-menu">
          {/* # Dashboard */}
          <button onClick={() => setActiveTabMobile("dashboard")} className={`nav-item ${activeTab === "dashboard" ? "active" : ""}`}><FiGrid /><span className="nav-text">Dashboard</span></button>
          {/* # My Profile */}
          <button onClick={() => setActiveTabMobile("profile")} className={`nav-item ${activeTab === "profile" ? "active" : ""}`}><FiUser /><span className="nav-text">My Profile</span></button>
          {/* # Availability */}
          <button onClick={() => setActiveTabMobile("availability")} className={`nav-item ${activeTab === "availability" ? "active" : ""}`}><FiCalendar /><span className="nav-text">Availability</span></button>
          {/* # Scheduled Calls */}
          <button onClick={() => setActiveTabMobile("calls")} className={`nav-item ${activeTab === "calls" ? "active" : ""}`}><FiClock /><span className="nav-text">Scheduled Calls</span></button>
          {/* # Earnings */}
          <button onClick={() => setActiveTabMobile("earnings")} className={`nav-item ${activeTab === "earnings" ? "active" : ""}`}><FiDollarSign /><span className="nav-text">Earnings</span></button>
          {/* # Consultation History */}
          <button onClick={() => setActiveTabMobile("history")} className={`nav-item ${activeTab === "history" ? "active" : ""}`}><FiFileText /><span className="nav-text">Consultation History</span></button>
          {/* # Reviews */}
          <button onClick={() => { setActiveTabMobile("reviews"); resetAndRefreshReviews(); }} className={`nav-item ${activeTab === "reviews" ? "active" : ""}`}><FiStar /><span className="nav-text">Reviews</span></button>
          {/* # Notifications */}
          <button onClick={() => setActiveTabMobile("notifications")} className={`nav-item ${activeTab === "notifications" ? "active" : ""}`}><FiBell /><span className="nav-text">Notifications</span> {unreadCount > 0 && <span className="badge">{unreadCount}</span>}</button>
          {/* # Settings */}
          <button onClick={() => setActiveTabMobile("settings")} className={`nav-item ${activeTab === "settings" ? "active" : ""}`}><FiSettings /><span className="nav-text">Settings</span></button>
        </nav>
        <button className="logout-btn" onClick={handleLogoutClick}><FiLogOut /> Logout</button>
      </aside>

      <main className="main-content">
        <header className="content-header">
          <h1>{activeTab === "dashboard" ? "Dashboard" : activeTab === "calls" ? "Scheduled Calls" : activeTab === "history" ? "Consultation History" : activeTab === "profile" ? "My Profile" : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h1>
          <div className="header-actions">
            <button className="icon-notification-btn" onClick={() => setActiveTab("notifications")}>
              <FiBell /> {unreadCount > 0 && <span className="dot"></span>}
            </button>
            <span className="header-actions-divider" aria-hidden="true"></span>
            <div
              className="user-pill clickable-user-pill"
              onClick={() => setActiveTab("profile")}
              role="button"
              tabIndex={0}
              title="Go to My Profile"
            >
              <div className="user-pill-avatar" style={{ background: avatarColorOf(profileForm.name) }}>
                {profileForm.profilePhoto && !photoLoadError ? <img src={profileForm.profilePhoto} alt="" onError={() => { console.warn("Profile photo failed to load:", profileForm.profilePhoto); setPhotoLoadError(true); }} /> : initialsOf(profileForm.name)}
              </div>
              <div className="user-pill-text">
                <span className="user-pill-name">{profileForm.name || "Expert"}</span>
                <span className="user-pill-sub">Expert</span>
              </div>
            </div>
          </div>
        </header>

        {renderTabContent({
          activeTab, stats, profileForm,
          isEditingProfile, setIsEditingProfile,
          handleProfileChange: (e) => setProfileForm({ ...profileForm, [e.target.name]: e.target.value }),
          handleSaveProfile,
          bankForm, isEditingBank, setIsEditingBank,
          handleBankFormChange: (e) => {
            const { name, value } = e.target;
            const nextValue = name === "ifsc" ? value.toUpperCase() : value;
            setBankForm({ ...bankForm, [name]: nextValue });
            if (name === "ifsc" && nextValue.length === 11) {
              getBankDetails(nextValue);
            }
          },
          handleSaveBankDetails,
          ifscLookupLoading,
          bankDocFile, handleBankDocChange, bankDocUploading, handleRemoveBankDoc,
          availabilityByDate, dayList, selectedDateKey, setSelectedDateKey,
          getDayState, handleToggleDate, handleToggleBlocked, handleAddSlot, handleAddMultipleSlots, handleRemoveSlot,
          addSlotFormOpen, setAddSlotFormOpen, addSlotStartTime, setAddSlotStartTime, formatSlotLabel, generateTimeOptions,
          reviews, loadingReviews, refreshReviews, resetAndRefreshReviews,
          notifications, setActiveTab,
          notificationFilter, setNotificationFilter,
          selectedNotification, handleNotificationClick, closeNotificationDetail,
          markAllNotificationsRead,
          scheduledCalls, activeCallFilter, setActiveCallFilter, handleCancelCall, handleJoinCall,
          historySearch, setHistorySearch, historyStatusFilter, setHistoryStatusFilter,
          settingsForm,
          handleSettingsChange: (n, v) => setSettingsForm({ ...settingsForm, [n]: v }),
          user, bankRequestSent, handleRequestBankUnlock, activeChatBooking, setActiveChatBooking,
          callDetailData, setCallDetailData,
          handleSaveAllSettings, isSavingSettings, handleDeleteAccount, handleDeactivateAccount, setRescheduleData,
          bankSuccessMsg, bankErrorMsg, deleteConfirmStep, setDeleteConfirmStep,
          showToast, handleProfilePhotoChange, profilePhotoUploading, photoLoadError, setPhotoLoadError,
          handleIndustryToggle, handleOpenEditProfile, handleCancelEditProfile, handleBioChange,
          ScrollArrowRow, availabilityScrollRef, callsFilterScrollRef
        })}
      </main>

      {/* ================================================================
          MOBILE BOTTOM NAVIGATION — Home / Calls / Earnings / Profile /
          More. "More" opens a bottom sheet with the remaining sections.
          ================================================================ */}
      <nav className="mobile-bottom-nav">
        <button onClick={() => setActiveTabMobile("dashboard")} className={`mobile-nav-item ${activeTab === "dashboard" ? "active" : ""}`}>
          <FiGrid /><span>Home</span>
        </button>
        <button onClick={() => setActiveTabMobile("calls")} className={`mobile-nav-item ${activeTab === "calls" ? "active" : ""}`}>
          <FiPhone /><span>Calls</span>
        </button>
        <button onClick={() => setActiveTabMobile("earnings")} className={`mobile-nav-item ${activeTab === "earnings" ? "active" : ""}`}>
          <FiDollarSign /><span>Earnings</span>
        </button>
        <button onClick={() => setActiveTabMobile("profile")} className={`mobile-nav-item ${activeTab === "profile" ? "active" : ""}`}>
          <FiUser /><span>Profile</span>
        </button>
        <button
          onClick={() => setMobileMoreOpen((v) => !v)}
          className={`mobile-nav-item ${MOBILE_MORE_TABS.includes(activeTab) || mobileMoreOpen ? "active" : ""}`}
        >
          <FiGrid style={{ transform: "rotate(45deg)" }} />
          <span>More</span>
          {unreadCount > 0 && <span className="mobile-nav-more-dot"></span>}
        </button>
      </nav>

      {mobileMoreOpen && (
        <div className="mobile-more-sheet">
          <div className="mobile-more-sheet-handle"></div>
          <button onClick={() => setActiveTabMobile("availability")} className={`mobile-more-item ${activeTab === "availability" ? "active" : ""}`}>
            <span className="mobile-more-item-icon"><FiCalendar /></span> Availability
          </button>
          <button onClick={() => setActiveTabMobile("history")} className={`mobile-more-item ${activeTab === "history" ? "active" : ""}`}>
            <span className="mobile-more-item-icon"><FiFileText /></span> Consultation History
          </button>
          <button onClick={() => setActiveTabMobile("reviews")} className={`mobile-more-item ${activeTab === "reviews" ? "active" : ""}`}>
            <span className="mobile-more-item-icon"><FiStar /></span> Reviews
          </button>
          <button onClick={() => setActiveTabMobile("notifications")} className={`mobile-more-item ${activeTab === "notifications" ? "active" : ""}`}>
            <span className="mobile-more-item-icon"><FiBell /></span> Notifications {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
          </button>
          <button onClick={() => setActiveTabMobile("settings")} className={`mobile-more-item ${activeTab === "settings" ? "active" : ""}`}>
            <span className="mobile-more-item-icon"><FiSettings /></span> Settings
          </button>
          <button onClick={handleLogoutClick} className="mobile-more-item mobile-more-logout">
            <span className="mobile-more-item-icon"><FiLogOut /></span> Logout
          </button>
        </div>
      )}
    </div>
  );
}

const NOTI_ICON = {
  booking: { icon: <FiBell />, bg: "#ede9fe", fg: "#7c3aed" },
  session: { icon: <FiCalendar />, bg: "#ccfbf1", fg: "#0d9488" },
  meeting: { icon: <FiVideo />, bg: "#ccfbf1", fg: "#0d9488" },
  payout: { icon: <FiDollarSign />, bg: "#dcfce7", fg: "#16a34a" },
  payment: { icon: <FiDollarSign />, bg: "#dcfce7", fg: "#16a34a" },
  reminder: { icon: <FiClock />, bg: "#fef3c7", fg: "#d97706" },
  message: { icon: <FiMessageCircle />, bg: "#fce7f3", fg: "#db2777" },
  verification: { icon: <FiShield />, bg: "#fef3c7", fg: "#d97706" }
};

const renderTabContent = (p) => {
  const {
    activeTab, stats, profileForm,
    isEditingProfile, setIsEditingProfile, handleProfileChange, handleSaveProfile,
    bankForm, isEditingBank, setIsEditingBank, handleBankFormChange, handleSaveBankDetails, ifscLookupLoading,
    bankDocFile, handleBankDocChange, bankDocUploading, handleRemoveBankDoc,
    availabilityByDate, dayList, selectedDateKey, setSelectedDateKey,
    getDayState, handleToggleDate, handleToggleBlocked, handleAddSlot, handleAddMultipleSlots, handleRemoveSlot,
    addSlotFormOpen, setAddSlotFormOpen, addSlotStartTime, setAddSlotStartTime, formatSlotLabel, generateTimeOptions,
    reviews, loadingReviews, refreshReviews, resetAndRefreshReviews,
    notifications, setActiveTab,
    notificationFilter, setNotificationFilter,
    selectedNotification, handleNotificationClick, closeNotificationDetail,
    markAllNotificationsRead,
    scheduledCalls, activeCallFilter, setActiveCallFilter, handleCancelCall, handleJoinCall,
    historySearch, setHistorySearch, historyStatusFilter, setHistoryStatusFilter,
    settingsForm, handleSettingsChange,
    user, bankRequestSent, handleRequestBankUnlock, activeChatBooking, setActiveChatBooking,
    callDetailData, setCallDetailData,
    handleSaveAllSettings, isSavingSettings, handleDeleteAccount, handleDeactivateAccount, setRescheduleData,
    bankSuccessMsg, bankErrorMsg, deleteConfirmStep, setDeleteConfirmStep,
    showToast, handleProfilePhotoChange, profilePhotoUploading, photoLoadError, setPhotoLoadError,
    handleIndustryToggle, handleOpenEditProfile, handleCancelEditProfile, handleBioChange,
    ScrollArrowRow, availabilityScrollRef, callsFilterScrollRef
  } = p;

  // ====================================================================
  // TAB CONTENT — one case per sidebar section, in the same order as
  // the sidebar nav buttons above. Each case returns the full JSX
  // for that section only.
  // ====================================================================
  switch (activeTab) {

    /* ============================================================
       # DASHBOARD
       Overview: today's calls, upcoming sessions, revenue &
       session charts, quick stats.
       ============================================================ */
    case "dashboard": {
      const todayKey = formatDateKey(new Date());
      const todaysCalls = scheduledCalls.filter(c => c.rawDate === todayKey && c.status !== "cancelled");
      const upcoming = scheduledCalls.filter(c => c.status === "upcoming" || c.status === "live").slice(0, 5);

      // last 6 months revenue from completed sessions
      const now = new Date();
      const months = Array.from({ length: 6 }).map((_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        return { year: d.getFullYear(), month: d.getMonth(), label: d.toLocaleString("en-IN", { month: "short" }) };
      });
      const completed = scheduledCalls.filter(c => c.status === "completed");
      const revenuePoints = months.map(m => {
        const value = completed.filter(c => {
          const d = parseSlotDateTime(c.rawDate, c.rawTime) || new Date(c.rawDate);
          return d.getFullYear() === m.year && d.getMonth() === m.month;
        }).reduce((sum, c) => sum + (Number(c.payout) || 0), 0);
        return { label: m.label, value };
      });
      const sessionPoints = months.map(m => {
        const value = completed.filter(c => {
          const d = parseSlotDateTime(c.rawDate, c.rawTime) || new Date(c.rawDate);
          return d.getFullYear() === m.year && d.getMonth() === m.month;
        }).length;
        return { label: m.label, value };
      });

      return (
        <>
          <section className="welcome-banner">
            <div className="welcome-banner-glow" />
            <h2>{getGreeting()}, {(profileForm.name || "Expert").split(" ")[0]}! 👋</h2>
            <p>
              {todaysCalls.length > 0
                ? `You have ${todaysCalls.length} consultation${todaysCalls.length > 1 ? "s" : ""} today. Stay prepared and make them count.`
                : "You have no consultations scheduled today. Use this time to update your availability."}
            </p>
            <div className="banner-buttons">
              <button className="primary-banner-btn" onClick={() => setActiveTab("calls")}>View Calls →</button>
              <button className="secondary-banner-btn" onClick={() => setActiveTab("availability")}>Manage Schedule</button>
            </div>
          </section>

          <section className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon-badge teal"><FiDollarSign /></div>
              <h2>₹{(stats.totalEarnings || 0).toLocaleString('en-IN')}</h2>
              <p>Total Earnings</p>
              <span className="metric-trend-tag positive">All time</span>
            </div>
            <div className="metric-card">
              <div className="metric-icon-badge indigo"><FiTrendingUp /></div>
              <h2>₹{(stats.monthEarnings || 0).toLocaleString('en-IN')}</h2>
              <p>This Month</p>
              <span className="metric-trend-tag positive">{stats.thisMonthSessions || 0} sessions</span>
            </div>
            <div className="metric-card">
              <div className="metric-icon-badge amber"><FiClock /></div>
              <h2>₹{(stats.pendingPayout || 0).toLocaleString('en-IN')}</h2>
              <p>Pending Payout</p>
              <span className="metric-trend-tag amber">Awaiting processing</span>
            </div>
            <div className="metric-card">
              <div className="metric-icon-badge pink"><FiUsers /></div>
              <h2>{stats.totalSessions || 0}</h2>
              <p>Total Sessions</p>
              <span className="metric-trend-tag pink">Lifetime completed</span>
            </div>
          </section>

          <section className="charts-grid">
            <div className="chart-card">
              <div className="chart-card-header">
                <div>
                  <h3>Monthly Revenue</h3>
                  <p>Last 6 months</p>
                </div>
              </div>
              <LineChartSVG points={revenuePoints} formatY={(v) => `₹${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
            </div>
            <div className="chart-card">
              <div className="chart-card-header">
                <div>
                  <h3>Sessions / Month</h3>
                  <p>Consultation volume</p>
                </div>
              </div>
              <BarChartSVG points={sessionPoints} />
            </div>
          </section>

          <section className="upcoming-section-card">
            <div className="upcoming-section-header">
              <h3>Upcoming Consultations</h3>
              <button className="view-all-link" onClick={() => setActiveTab("calls")}>View all →</button>
            </div>
            {upcoming.length === 0 ? (
              <div className="empty-state-box">No upcoming consultations scheduled.</div>
            ) : (
              <div className="upcoming-list">
                {upcoming.map((c) => (
                  <div key={c.id} className="upcoming-row">
                    <div className="upcoming-row-avatar" style={{ background: avatarColorOf(c.clientName) }}>{initialsOf(c.clientName)}</div>
                    <div className="upcoming-row-meta">
                      <h4>{c.clientName}</h4>
                      <p>{c.clientCompany} · {c.topic}</p>
                      <span className="upcoming-row-date"><FiCalendar size={12} /> {c.date}{c.time ? `, ${c.time}` : ""}</span>
                    </div>
                    <div className="upcoming-row-actions">
                      <span className="time-until-pill">{c.status === "live" ? "Live now" : c.countdownText}</span>
                      <button className="join-btn-sm" onClick={() => {
                        if (c.sessionType === "Call") {
                          handleJoinCall(c);
                        } else {
                          setActiveChatBooking(c); setActiveTab("calls");
                        }
                      }}>{c.sessionType === "Call" ? "📹 Join Call" : "💬 Join Chat"}</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      );
    }

    /* ============================================================
       # MY PROFILE
       Editable expert profile: personal info, bio, industry,
       plus bank details sub-section for payouts.
       ============================================================ */
    case "profile": {
      const sessionsCompleted = scheduledCalls.filter(c => c.status === "completed").length;
      return (
        <div className="profile-view-wrapper">
          <form onSubmit={handleSaveProfile}>
            <div className="profile-hero-banner-styled">
              {profileForm.approvalStatus === "approved" && <span className="verified-pill">✓ Verified Expert</span>}
              {!isEditingProfile ? (
                <button type="button" className="action-styled-btn theme-light position-bottom-right" onClick={handleOpenEditProfile}>✎ Edit Profile</button>
              ) : (
                <div className="profile-action-btn-group">
                  <button type="submit" className="action-styled-btn theme-accent">Save Changes</button>
                  <button type="button" onClick={handleCancelEditProfile} className="action-styled-btn theme-muted">Cancel</button>
                </div>
              )}
              <div className="profile-hero-avatar-wrap">
                <div className="profile-hero-avatar" style={{ background: avatarColorOf(profileForm.name) }}>
                  {profileForm.profilePhoto && !photoLoadError ? <img src={profileForm.profilePhoto} alt="" onError={() => { console.warn("Profile photo failed to load:", profileForm.profilePhoto); setPhotoLoadError(true); }} /> : initialsOf(profileForm.name)}
                  {profilePhotoUploading && <div className="avatar-uploading-overlay">…</div>}
                </div>
                <label className="avatar-photo-edit-badge" htmlFor="profilePhotoUploadInput" title="Change profile photo">
                  📷
                </label>
                <input
                  id="profilePhotoUploadInput"
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleProfilePhotoChange}
                  style={{ display: "none" }}
                />
              </div>
            </div>

            <div className="profile-identity-row">
              <div>
                {!isEditingProfile ? (
                  <>
                    <h2 className="profile-identity-name">{profileForm.name || "—"}</h2>
                    <p className="profile-identity-company"><FiBriefcase size={13} /> {profileForm.company || "—"}</p>
                  </>
                ) : (
                  <div className="profile-identity-edit-fields">
                    <div>
                      <label className="bank-form-input-label">Full Name</label>
                      <input type="text" name="name" value={profileForm.name} onChange={handleProfileChange} placeholder="Full Name" className="dashboard-input-control-styled" />
                    </div>
                    <div>
                      <label className="bank-form-input-label">Company</label>
                      <input type="text" name="company" value={profileForm.company} onChange={handleProfileChange} placeholder="Company" className="dashboard-input-control-styled" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="profile-stats-row">
              <div className="profile-stat-cell">
                <h3>{stats.rating > 0 ? `${stats.rating.toFixed(1)} ★` : "—"}</h3>
                <p>Rating</p>
              </div>
              <div className="profile-stat-cell">
                <h3>{stats.reviewsCount || 0}</h3>
                <p>Reviews</p>
              </div>
              <div className="profile-stat-cell">
                <h3>{sessionsCompleted}</h3>
                <p>Sessions</p>
              </div>
            </div>

            <div className="profile-tags-row">
              {profileForm.industry?.length > 0 && profileForm.industry.map((tag, i) => <span key={i} className="profile-tag-pill">{tag}</span>)}
              {profileForm.experienceYears && <span className="profile-tag-pill">{profileForm.experienceYears} Years Experience</span>}
              {profileForm.location && <span className="profile-tag-pill">{profileForm.location.split(",")[0]}</span>}
            </div>

            <div className="info-block-card description-card-margin">
              <h3 className="info-card-title-header">Professional Bio</h3>
              {!isEditingProfile ? (
                <p className="profile-view-paragraph">{profileForm.bio || "No biography added yet."}</p>
              ) : (
                <>
                  <textarea
                    name="bio"
                    value={profileForm.bio}
                    onChange={handleBioChange}
                    maxLength={2000}
                    rows={5}
                    className="dashboard-textarea-control-styled"
                  />
                  <div className="bio-limit-counter-row">
                    <span className={(profileForm.bio.trim() ? profileForm.bio.trim().split(/\s+/).filter(Boolean).length : 0) >= 200 ? "bio-limit-at-max" : ""}>
                      {profileForm.bio.trim() ? profileForm.bio.trim().split(/\s+/).filter(Boolean).length : 0} / 200 words
                    </span>
                    <span className={profileForm.bio.length >= 2000 ? "bio-limit-at-max" : ""}>
                      {profileForm.bio.length} / 2000 characters
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="contact-info-grid">
              <div className="contact-info-cell">
                <div className="contact-info-icon"><FiMail /></div>
                <div>
                  <span className="contact-info-label">Email</span>
                  <div className="contact-info-value">{profileForm.email || "—"}</div>
                </div>
              </div>
              <div className="contact-info-cell">
                <div className="contact-info-icon"><FiPhone /></div>
                <div>
                  <span className="contact-info-label">Phone</span>
                  {!isEditingProfile ? <div className="contact-info-value">{profileForm.phone || "—"}</div> : <input type="text" name="phone" value={profileForm.phone} onChange={handleProfileChange} className="dashboard-input-control-styled" />}
                </div>
              </div>
              <div className="contact-info-cell">
                <div className="contact-info-icon"><FiMapPin /></div>
                <div>
                  <span className="contact-info-label">Location</span>
                  {!isEditingProfile ? <div className="contact-info-value">{profileForm.location || "—"}</div> : <input type="text" name="location" value={profileForm.location} onChange={handleProfileChange} className="dashboard-input-control-styled" />}
                </div>
              </div>
              <div className="contact-info-cell industry-contact-cell">
                <div className="contact-info-icon"><FiBriefcase /></div>
                <div className="industry-contact-cell-body">
                  <span className="contact-info-label">Industry <span className="optional-field-tag">{isEditingProfile ? `(select up to ${MAX_INDUSTRY_SELECTIONS})` : ""}</span></span>
                  {!isEditingProfile ? (
                    <div className="contact-info-value">{profileForm.industry?.join(", ") || "—"}</div>
                  ) : (
                    <>
                      <select
                        value=""
                        onChange={(e) => { if (e.target.value) handleIndustryToggle(e.target.value); }}
                        disabled={(profileForm.industry || []).length >= MAX_INDUSTRY_SELECTIONS}
                        className="dashboard-input-control-styled"
                      >
                        <option value="">
                          {(profileForm.industry || []).length >= MAX_INDUSTRY_SELECTIONS ? "Maximum 2 selected" : "Select industry..."}
                        </option>
                        {INDUSTRY_OPTIONS
                          .filter((ind) => !(profileForm.industry || []).includes(ind))
                          .map((ind) => (
                            <option key={ind} value={ind}>{ind}</option>
                          ))}
                      </select>
                      {profileForm.industry?.length > 0 && (
                        <div className="industry-selected-pills-row">
                          {profileForm.industry.map((ind) => (
                            <span key={ind} className="profile-tag-pill removable-industry-pill">
                              {ind}
                              <button type="button" onClick={() => handleIndustryToggle(ind)} aria-label={`Remove ${ind}`}>×</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </form>

          <div className="info-block-card bank-ledger-margin">
            <div className="bank-ledger-header-wrapper">
              <h3 className="info-card-title-header ledger-title-no-margin">
                <FiCreditCard size={16} /> {isEditingBank ? "Update Bank Details" : "Bank Details"}
              </h3>
              {!isEditingBank ? (
                <>
                  {!bankForm.holder ? (
                    <button type="button" onClick={() => setIsEditingBank(true)} className="action-styled-btn theme-secondary">Setup Payout Details</button>
                  ) : profileForm.bankEditStatus === "approved" ? (
                    <button type="button" onClick={() => setIsEditingBank(true)} className="action-styled-btn theme-secondary">Edit</button>
                  ) : profileForm.bankEditStatus === "pending_approval" ? (
                    <span className="bank-pending-review-label">Pending Admin Approval</span>
                  ) : bankRequestSent || profileForm.bankEditStatus === "requested" ? (
                    <span className="bank-pending-review-label">Unlock Request Pending</span>
                  ) : (
                    <button type="button" onClick={handleRequestBankUnlock} className="action-styled-btn theme-danger">Request Edit Unlock</button>
                  )}
                </>
              ) : null}
            </div>
            <p className="bank-ledger-subtitle">For receiving your consultation payouts</p>

            {!isEditingBank ? (
              <div className="bank-summary-display-panel">
                {bankForm.holder ? (
                  <div className="bank-summary-grid-layout">
                    <div><span className="bank-meta-label-tag">Account Holder</span><div className="bank-meta-value-tag"><strong>{bankForm.holder || "—"}</strong></div></div>
                    <div><span className="bank-meta-label-tag">Account Number</span><div className="bank-meta-value-tag">XXXX XXXX {bankForm.accountNum.slice(-4)}</div></div>
                    <div><span className="bank-meta-label-tag">Bank Name</span><div className="bank-meta-value-tag">{bankForm.bankName || "—"}</div></div>
                    <div>
                      <span className="bank-meta-label-tag">IFSC Code</span>
                      <div className="bank-meta-value-tag">{bankForm.ifsc || "—"}</div>
                    </div>
                    <div>
                      <span className="bank-meta-label-tag">Branch</span>
                      <div className="bank-meta-value-tag">{bankForm.branch || "—"}</div>
                    </div>
                    <div>
                      <span className="bank-meta-label-tag">Location</span>
                      <div className="bank-meta-value-tag">{bankForm.location || "—"}</div>
                    </div>
                    {bankForm.upi && (
                      <div>
                        <span className="bank-meta-label-tag">UPI ID</span>
                        <div className="bank-meta-value-tag">{bankForm.upi}</div>
                      </div>
                    )}
                    <div>
                      <span className="bank-meta-label-tag">Verification Document</span>
                      <div className="bank-meta-value-tag">
                        {bankForm.docUrl ? (
                          <a href={bankForm.docUrl} target="_blank" rel="noopener noreferrer" className="bank-doc-view-link">📄 View uploaded document</a>
                        ) : "—"}
                      </div>
                    </div>
                    {!profileForm.isVisible ? null : (
                      <div className="bank-verified-cell">
                        <FiCheckCircle /> <div><strong>Verified</strong><span>Bank account verified</span></div>
                      </div>
                    )}
                  </div>
                ) : (<p className="bank-empty-ledger-note">No bank account connected yet. Click "Setup Payout Details" to add yours.</p>)}

                {bankForm.holder && (
                  // isVisible is the true "everything's approved and live" signal now:
                  // account approved + bank details approved + session fee & cut % assigned
                  // (see the admin's "Set & Publish" action, the only place isVisible is
                  // ever set to true). approvalStatus alone only means the account itself
                  // was approved — it no longer implies visibility to clients.
                  <div className={`approval-status-banner ${profileForm.isVisible ? "approved" : "pending"}`}>
                    {profileForm.isVisible ? (
                      <>
                        <p className="approval-status-title">✅ Approved & Visible to Clients</p>
                        <p className="approval-status-sub">Your profile is live. Clients can now book sessions with you.</p>
                      </>
                    ) : (
                      <>
                        <p className="approval-status-title">⏳ Pending Admin Approval</p>
                        <p className="approval-status-sub">Admin will review your bank details, assign your session fee, and make you visible to clients.</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="bank-update-card-redesigned">
                <div className="bank-edit-form-grid">
                  <div>
                    <label className="bank-form-input-label">Account Holder Name</label>
                    <input type="text" name="holder" value={bankForm.holder} onChange={handleBankFormChange} placeholder="Account Holder Name" className="dashboard-input-control-styled" />
                  </div>
                  <div>
                    <label className="bank-form-input-label">Account Number</label>
                    <input type="text" name="accountNum" value={bankForm.accountNum.startsWith("••") ? "" : bankForm.accountNum} onChange={handleBankFormChange} placeholder="Account Number" className="dashboard-input-control-styled" />
                  </div>
                  <div>
                    <label className="bank-form-input-label">Re-enter Account Number</label>
                    <input type="text" name="confirmAccountNum" value={bankForm.confirmAccountNum.startsWith("••") ? "" : bankForm.confirmAccountNum} onChange={handleBankFormChange} placeholder="Re-enter Account Number" className="dashboard-input-control-styled" />
                  </div>
                  <div>
                    <label className="bank-form-input-label">Bank Name</label>
                    <input type="text" name="bankName" value={bankForm.bankName} onChange={handleBankFormChange} placeholder="Bank Name" className="dashboard-input-control-styled" />
                  </div>
                  <div className="bank-ifsc-field-wrap">
                    <label className="bank-form-input-label">IFSC Code</label>
                    <input type="text" name="ifsc" value={bankForm.ifsc} maxLength={11} onChange={handleBankFormChange} placeholder="IFSC Code" className="dashboard-input-control-styled" />
                    {ifscLookupLoading && <span className="ifsc-lookup-status">Verifying IFSC…</span>}
                    {!ifscLookupLoading && bankForm.ifsc.length === 11 && bankForm.bankName && (
                      <div className="ifsc-fetched-success-note">
                        ✓ {bankForm.bankName}{bankForm.branch ? `, ${bankForm.branch}` : ""}{bankForm.location ? ` — ${bankForm.location}` : ""}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="bank-form-input-label">UPI ID <span className="optional-field-tag">(optional)</span></label>
                    <input type="text" name="upi" value={bankForm.upi} onChange={handleBankFormChange} placeholder="yourname@upi" className="dashboard-input-control-styled" />
                  </div>
                </div>

                <div className={`bank-doc-dropzone-field ${(bankDocFile || bankForm.docUrl) ? "has-file" : ""}`}>
                  <label className="bank-form-input-label">
                    Upload Passbook / Cancelled Cheque <span className="required-asterisk">* Mandatory</span>
                  </label>
                  <p className="bank-doc-upload-hint">
                    Accepted documents: a photo/scan of your bank passbook front page, a cancelled cheque, or any bank-issued document (e.g. account statement) that clearly shows your <strong>account number</strong> and <strong>account holder name</strong>. JPG, PNG, or PDF, up to 30MB.
                  </p>
                  <label className="bank-doc-dropzone-box" htmlFor="bankDocUploadInput">
                    <span className="bank-doc-dropzone-icon">{(bankDocFile || bankForm.docUrl) ? "✅" : "⬆️"}</span>
                    {bankDocFile ? (
                      <span className="bank-doc-selected-name">Selected: {bankDocFile.name}</span>
                    ) : bankForm.docUrl ? (
                      <span className="bank-doc-selected-name">Document uploaded — click to replace</span>
                    ) : (
                      <span className="bank-doc-dropzone-text">Click to upload your document<br /><span className="bank-doc-dropzone-subtext">JPG, PNG or PDF, up to 30MB</span></span>
                    )}
                  </label>
                  <input id="bankDocUploadInput" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={handleBankDocChange} style={{ display: "none" }} />
                  {(bankDocFile || bankForm.docUrl) && (
                    <button type="button" className="bank-doc-remove-btn" onClick={handleRemoveBankDoc}>✕ Remove file</button>
                  )}
                  {bankDocUploading && <span className="ifsc-lookup-status">Uploading document…</span>}
                  {!bankDocFile && !bankForm.docUrl && (
                    <p className="bank-doc-mandatory-warning">A verification document is required before you can save your bank details.</p>
                  )}
                </div>

                <div className="bank-editing-btn-actions bank-update-actions-row">
                  <button
                    type="button"
                    onClick={handleSaveBankDetails}
                    disabled={(!bankDocFile && !bankForm.docUrl) || bankDocUploading}
                    className="action-styled-btn theme-accent"
                    title={(!bankDocFile && !bankForm.docUrl) ? "Please upload your passbook or cancelled cheque first" : ""}
                  >
                    {bankDocUploading ? "Uploading…" : "Save Changes"}
                  </button>
                  <button type="button" onClick={() => setIsEditingBank(false)} className="action-styled-btn theme-muted">Cancel</button>
                </div>
              </div>
            )}

            {bankErrorMsg && <div className="inline-alert error">⚠️ {bankErrorMsg}</div>}
            {bankSuccessMsg && <div className="inline-alert success">{bankSuccessMsg}</div>}
          </div>
        </div>
      );
    }

    /* ============================================================
       # AVAILABILITY
       Rolling 30-day calendar. Toggle days active/blocked and
       manage bookable time slots for each date.
       ============================================================ */
    case "availability": {
      const selected = getDayState(selectedDateKey);
      const selectedDateObj = dayList.find(d => formatDateKey(d) === selectedDateKey) || dayList[0];
      const selectedDayName = selectedDateObj.toLocaleDateString("en-IN", { weekday: "long" });
      const selectedCount = addSlotStartTime.length;
      const visibleSelectedSlots = (selected.slots || [])
        .map((slot, idx) => ({ slot, idx }))
        .filter(({ slot }) => !isSlotPast(selectedDateKey, slot));

      return (
        <div className="availability-workspace-card">
          <div className="availability-header-row">
            <div>
              <h3 className="availability-main-title">Availability</h3>
              <p className="availability-sub-title">Today through the next 30 days · each session slot is {SLOT_MINUTES} minutes</p>
            </div>
            {profileForm.consultationFee ? (
              <span className="rate-pill-inline">₹{Number((profileForm.consultationFee * (profileForm.expertCutPercent ?? 100)) / 100).toLocaleString('en-IN')} / {SLOT_MINUTES} min</span>
            ) : null}
          </div>

          <ScrollArrowRow className="availability-30day-scroll" scrollRef={availabilityScrollRef}>
            {dayList.map((d) => {
              const key = formatDateKey(d);
              const state = getDayState(key);
              const isToday = key === formatDateKey(new Date());
              const visibleSlotCount = (state.slots || []).filter(s => !isSlotPast(key, s)).length;
              return (
                <button key={key} onClick={() => { setSelectedDateKey(key); setAddSlotFormOpen(false); }} className={`availability-day-selector-btn ${selectedDateKey === key ? "selected" : ""} ${isToday ? "is-today" : ""}`}>
                  <div className="availability-day-short-name">{d.toLocaleDateString("en-IN", { weekday: "short" })}</div>
                  <div className="availability-day-date-num">{d.getDate()}</div>
                  <div className={`availability-day-slots-counter ${state.blocked ? "blocked-label" : ""}`}>
                    {state.blocked ? "blocked" : state.active ? `${visibleSlotCount} slots` : "off"}
                  </div>
                </button>
              );
            })}
          </ScrollArrowRow>

          <div className="availability-operations-panel">
            <div className="availability-panel-header">
              <h3 className="availability-panel-title">
                {selectedDateObj.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
                {" "}{!selected.blocked && visibleSelectedSlots.length > 0 ? `· ${visibleSelectedSlots.length} slots available` : ""}
              </h3>
              <div className="availability-dual-toggle-group">
                <label className="availability-toggle-active-label">
                  <input type="checkbox" checked={!!selected.active} onChange={() => handleToggleDate(selectedDateKey)} className="toggle-switch-input teal" /> Available
                </label>
                <label className="availability-toggle-active-label danger">
                  <input type="checkbox" checked={!!selected.blocked} onChange={() => handleToggleBlocked(selectedDateKey)} className="toggle-switch-input red" /> Block Holiday
                </label>
              </div>
            </div>

            {selected.blocked ? (
              <div className="availability-holiday-blocked-state">
                <div className="availability-holiday-x-icon">✕</div>
                <h4>This day is blocked as a holiday</h4>
                <p>No consultations will be scheduled on {selectedDayName}</p>
              </div>
            ) : (
              <div className="availability-slots-interactive-area">
                {visibleSelectedSlots.length === 0 ? (
                  <p className="no-slots-alert">No time slots added for this date yet.</p>
                ) : (
                  <div className="availability-active-slots-row">
                    {visibleSelectedSlots.map(({ slot, idx }) => (
                      <div key={idx} className="availability-slot-badge-token">
                        <FiClock size={12} className="availability-slot-clock-icon" />
                        <span className="availability-slot-time-text">{slot}</span>
                        <span onClick={() => handleRemoveSlot(selectedDateKey, idx)} className="availability-remove-slot-trigger">✕</span>
                      </div>
                    ))}
                  </div>
                )}

                {!addSlotFormOpen ? (
                  <button onClick={() => { setAddSlotFormOpen(true); setAddSlotStartTime([]); }} className="action-styled-btn theme-secondary">+ Add Time Slot</button>
                ) : (
                  <div className="availability-add-slot-form-block">
                    <label className="bank-form-input-label">Select one or more time slots</label>
                    <div className="availability-time-pill-grid">
                      {generateTimeOptions().filter((opt) => !isSlotPast(selectedDateKey, opt.label)).map((opt) => {
                        const alreadyAdded = (selected.slots || []).includes(opt.label);
                        const isPicked = addSlotStartTime.includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            disabled={alreadyAdded}
                            onClick={() => {
                              setAddSlotStartTime((prev) =>
                                prev.includes(opt.value) ? prev.filter((v) => v !== opt.value) : [...prev, opt.value]
                              );
                            }}
                            className={`availability-time-pill-btn ${isPicked ? "selected" : ""} ${alreadyAdded ? "booked" : ""}`}
                          >
                            {opt.shortLabel}
                          </button>
                        );
                      })}
                    </div>

                    <div className="availability-add-slot-form-row">
                      <span className="availability-selected-count-label">
                        {selectedCount > 0 ? `${selectedCount} time slot${selectedCount > 1 ? "s" : ""} selected` : "No time slots selected"}
                      </span>
                      <button
                        onClick={() => {
                          if (addSlotStartTime.length) {
                            handleAddMultipleSlots(selectedDateKey, addSlotStartTime);
                            setAddSlotFormOpen(false);
                            setAddSlotStartTime([]);
                          }
                        }}
                        disabled={!addSlotStartTime.length}
                        className="action-styled-btn theme-accent"
                      >✓ Add {selectedCount > 1 ? `${selectedCount} Slots` : "Slot"}</button>
                      <button onClick={() => { setAddSlotFormOpen(false); setAddSlotStartTime([]); }} className="action-styled-btn theme-muted">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="availability-summary-section">
            <h3 className="availability-summary-title">Next 30 Days Summary</h3>
            <div className="availability-summary-grid">
              {dayList.map((d) => {
                const key = formatDateKey(d);
                const state = getDayState(key);
                const visibleSlots = (state.slots || []).filter(s => !isSlotPast(key, s));
                return (
                  <div key={key} className={`availability-summary-card ${state.blocked ? "is-holiday" : ""}`}>
                    <div className="availability-summary-card-head">
                      <strong>{d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</strong>
                      {state.blocked ? (
                        <span className="availability-summary-holiday-pill">Holiday</span>
                      ) : state.active && visibleSlots.length > 0 ? (
                        <span className="availability-summary-count-pill">{visibleSlots.length} slots</span>
                      ) : (
                        <span className="availability-summary-off-label">Off</span>
                      )}
                    </div>
                    {!state.blocked && state.active && visibleSlots.length > 0 && (
                      <div className="availability-summary-slot-list">
                        {visibleSlots.map((s, i) => <span key={i}>{s}</span>)}
                      </div>
                    )}
                    {state.blocked && (
                      <p className="availability-summary-holiday-note">No consultations scheduled</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    /* ============================================================
       # SCHEDULED CALLS
       Upcoming / live / past sessions with filters, chat, and
       reschedule actions.
       ============================================================ */
    case "calls": {
      if (activeChatBooking) {
        return (<ExpertChatView booking={activeChatBooking} expertUser={user} onBack={() => setActiveChatBooking(null)} />);
      }
      const counts = {
        All: scheduledCalls.filter(c => c.status !== "cancelled").length,
        Live: scheduledCalls.filter(c => c.status === "live").length,
        Upcoming: scheduledCalls.filter(c => c.status === "upcoming").length,
        Completed: scheduledCalls.filter(c => c.status === "completed").length,
        Cancelled: scheduledCalls.filter(c => c.status === "cancelled").length,
      };
      const filteredCalls = scheduledCalls.filter(call => {
        if (activeCallFilter === "All") return call.status !== "cancelled";
        return call.status === activeCallFilter.toLowerCase();
      });

      return (
        <div className="calls-tab-wrapper-layout">
          <ScrollArrowRow className="calls-filter-buttons-row" scrollRef={callsFilterScrollRef}>
            {["All", "Live", "Upcoming", "Completed", "Cancelled"].map((f) => (
              <button key={f} onClick={() => setActiveCallFilter(f)} className={`calls-filter-pill-btn ${activeCallFilter === f ? "active" : ""}`}>{f} ({counts[f]})</button>
            ))}
          </ScrollArrowRow>

          <div className="calls-cards-vertical-stack">
            {filteredCalls.length === 0 ? (
              <div className="empty-state-box">No calls found for this filter.</div>
            ) : (
              filteredCalls.map((call) => (
                <div key={call.id} className={`calls-appointment-item-card ${call.status === "live" ? "is-live" : ""}`}>
                  {call.status === "live" && <div className="live-banner-tag">● SESSION IS LIVE</div>}
                  <div className="calls-card-top-line">
                    <div className="calls-card-identity">
                      <div className="calls-card-avatar" style={{ background: avatarColorOf(call.clientName) }}>{initialsOf(call.clientName)}</div>
                      <div>
                        <h3 className="calls-card-client-title">{call.clientName}</h3>
                      </div>
                    </div>
                    <span className={`calls-card-status-pill-badge status-${call.status}`}>
                      {call.status === "live" ? "Live Now" : call.status.charAt(0).toUpperCase() + call.status.slice(1)}
                    </span>
                  </div>
                  <div className="calls-card-topic-quote">"{call.topic}"</div>
                  <div className="calls-card-timeline-metrics">
                    <span><FiCalendar size={13} /> {call.date}</span>
                    <span><FiClock size={13} /> {call.time} · {call.duration}</span>
                  </div>
                  {call.status !== "completed" && call.status !== "cancelled" && (
                    <p className="calls-card-starts-in">Starts in: <strong>{call.status === "live" ? "Live now" : call.countdownText}</strong></p>
                  )}

                  <div className="calls-card-interactive-actions">
                    {call.status === "live" ? (
                      call.sessionType === "Call" ? (
                        <button onClick={() => handleJoinCall(call)} className="action-styled-btn theme-live">📹 Join Live Call</button>
                      ) : (
                        <button onClick={() => setActiveChatBooking(call)} className="action-styled-btn theme-live">💬 Join Live Chat</button>
                      )
                    ) : call.status === "upcoming" ? (
                      <>
                        {call.sessionType === "Call" ? (
                          <button onClick={() => handleJoinCall(call)} className="action-styled-btn theme-secondary">📹 Join Call</button>
                        ) : (
                          <button onClick={() => setActiveChatBooking(call)} className="action-styled-btn theme-secondary">💬 Join Chat</button>
                        )}
                        <button onClick={() => setRescheduleData({ show: true, bookingId: call.id, date: call.date, time: "" })} className="action-styled-btn theme-muted">Reschedule</button>
                        <button onClick={() => handleCancelCall(call)} className="action-styled-btn theme-danger">Cancel</button>
                      </>
                    ) : null}
                    <button onClick={() => setCallDetailData(call)} className="action-styled-btn theme-outline">Details</button>
                  </div>
                </div>
              ))
            )}
          </div>

          {callDetailData && (
            <div className="reschedule-modal-overlay" onClick={() => setCallDetailData(null)}>
              <div className="reschedule-modal-card" onClick={(e) => e.stopPropagation()}>
                <h3 className="reschedule-modal-title">Booking Details</h3>
                <p className="reschedule-modal-subtitle">{callDetailData.date} · {callDetailData.time}</p>

                {callDetailData.topic && (
                  <div className="calls-card-topic-quote">"{callDetailData.topic}"</div>
                )}

                <div className="reschedule-modal-fields">
                  <div><strong>Client:</strong> {callDetailData.clientName || "—"}</div>
                  <div><strong>Duration:</strong> {callDetailData.duration || "—"}</div>
                  <div><strong>Session type:</strong> {callDetailData.sessionType || "—"}</div>
                  <div><strong>Status:</strong> {callDetailData.status ? callDetailData.status.charAt(0).toUpperCase() + callDetailData.status.slice(1) : "—"}</div>
                </div>

                <div className="reschedule-modal-actions">
                  <button className="reschedule-confirm-btn" onClick={() => setCallDetailData(null)}>Close</button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    /* ============================================================
       # EARNINGS
       Total / monthly / pending payout stats and earnings
       breakdown chart.
       ============================================================ */
    case "earnings": {
      const completed = scheduledCalls.filter(c => c.status === "completed");
      const cancelled = scheduledCalls.filter(c => c.status === "cancelled");

      const now = new Date();
      const months = Array.from({ length: 6 }).map((_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        return { year: d.getFullYear(), month: d.getMonth(), label: d.toLocaleString("en-IN", { month: "short" }) };
      });
      const revenuePoints = months.map(m => {
        const value = completed.filter(c => {
          const d = parseSlotDateTime(c.rawDate, c.rawTime) || new Date(c.rawDate);
          return d.getFullYear() === m.year && d.getMonth() === m.month;
        }).reduce((sum, c) => sum + (Number(c.payout) || 0), 0);
        return { label: m.label, value };
      });

      const payoutByMonth = {};
      completed.forEach(c => {
        const d = parseSlotDateTime(c.rawDate, c.rawTime) || new Date(c.rawDate);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        if (!payoutByMonth[key]) payoutByMonth[key] = { label: d.toLocaleString("en-IN", { month: "long", year: "numeric" }), total: 0, count: 0, date: d };
        payoutByMonth[key].total += Number(c.payout) || 0;
        payoutByMonth[key].count += 1;
      });
      const payoutHistory = Object.values(payoutByMonth).sort((a, b) => b.date - a.date);

      return (
        <div className="earnings-tab-container-layout">
          <section className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon-badge teal"><FiDollarSign /></div>
              <h2>₹{(stats.totalEarnings || 0).toLocaleString('en-IN')}</h2>
              <p>Total Earnings</p>
              <span className="metric-trend-tag positive">All time</span>
            </div>
            <div className="metric-card">
              <div className="metric-icon-badge indigo"><FiTrendingUp /></div>
              <h2>₹{(stats.monthEarnings || 0).toLocaleString('en-IN')}</h2>
              <p>This Month</p>
              <span className="metric-trend-tag positive">{stats.thisMonthSessions || 0} sessions</span>
            </div>
            <div className="metric-card">
              <div className="metric-icon-badge amber"><FiClock /></div>
              <h2>₹{(stats.pendingPayout || 0).toLocaleString('en-IN')}</h2>
              <p>Pending Payout</p>
              <span className="metric-trend-tag amber">Awaiting processing</span>
            </div>
            <div className="metric-card">
              <div className="metric-icon-badge pink"><FiStar /></div>
              <h2>{stats.rating > 0 ? stats.rating.toFixed(1) : "—"}</h2>
              <p>Avg Rating</p>
              <span className="metric-trend-tag pink">{stats.reviewsCount || 0} reviews</span>
            </div>
          </section>

          <section className="charts-grid">
            <div className="chart-card">
              <div className="chart-card-header">
                <div><h3>Revenue Trend</h3><p>Monthly earnings over last 6 months</p></div>
              </div>
              <LineChartSVG points={revenuePoints} formatY={(v) => `₹${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
            </div>
            <div className="chart-card">
              <div className="chart-card-header">
                <div><h3>Session Breakdown</h3><p>Lifetime — {scheduledCalls.length} total</p></div>
              </div>
              <div className="donut-card-body">
                <DonutChartSVG segments={[
                  { label: "Completed", value: completed.length, color: "#0d9488" },
                  { label: "Cancelled", value: cancelled.length, color: "#ef4444" },
                ]} />
                <div className="donut-legend">
                  <div className="donut-legend-row"><span className="dot" style={{ background: "#0d9488" }} /> Completed <strong>{completed.length}</strong></div>
                  <div className="donut-legend-row"><span className="dot" style={{ background: "#ef4444" }} /> Cancelled <strong>{cancelled.length}</strong></div>
                </div>
              </div>
            </div>
          </section>

          <section className="upcoming-section-card">
            <div className="upcoming-section-header"><h3>Payout History</h3></div>
            {bankForm.bankName && <p className="payout-history-sub">Credited to {bankForm.bankName} ••••{bankForm.accountNum.slice(-4)}</p>}
            {payoutHistory.length === 0 ? (
              <div className="empty-state-box">No payouts recorded yet.</div>
            ) : (
              <div className="payout-history-list">
                {payoutHistory.map((row, idx) => (
                  <div key={idx} className="payout-history-row">
                    <div className="payout-history-icon"><FiDollarSign /></div>
                    <div className="payout-history-meta">
                      <strong>{row.label}</strong>
                      <span>{row.count} session{row.count > 1 ? "s" : ""}</span>
                    </div>
                    <div className="payout-history-amount">
                      <strong>₹{row.total.toLocaleString('en-IN')}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      );
    }

    /* ============================================================
       # CONSULTATION HISTORY
       Searchable/filterable table of all past consultations.
       ============================================================ */
    case "history": {
      const completed = scheduledCalls.filter(c => c.status === "completed").length;
      const totalEarned = scheduledCalls.filter(c => c.status === "completed").reduce((s, c) => s + (Number(c.payout) || 0), 0);

      let rows = scheduledCalls;
      if (historyStatusFilter !== "All Status") {
        rows = rows.filter(r => r.status === historyStatusFilter.toLowerCase());
      }
      if (historySearch.trim()) {
        const q = historySearch.trim().toLowerCase();
        rows = rows.filter(r => (r.clientName || "").toLowerCase().includes(q) || (r.clientCompany || "").toLowerCase().includes(q) || (r.topic || "").toLowerCase().includes(q));
      }

      return (
        <div className="history-tab-table-wrapper">
          <section className="metrics-grid metrics-grid-3">
            <div className="metric-card"><h2>{completed}</h2><p>Total Completed</p></div>
            <div className="metric-card"><h2>₹{totalEarned.toLocaleString('en-IN')}</h2><p>Earned (Shown)</p></div>
            <div className="metric-card"><h2>{stats.rating > 0 ? stats.rating.toFixed(1) : "—"}</h2><p>Avg Rating</p></div>
          </section>

          <div className="history-search-row">
            <div className="history-search-input-wrap">
              <FiSearch />
              <input type="text" placeholder="Search by client, startup, or topic..." value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} />
            </div>
            <select value={historyStatusFilter} onChange={(e) => setHistoryStatusFilter(e.target.value)} className="history-status-select">
              <option>All Status</option>
              <option>Completed</option>
              <option>Cancelled</option>
              <option>Upcoming</option>
            </select>
          </div>

          {rows.length === 0 ? (
            <div className="empty-state-box">No consultations found.</div>
          ) : (
            <table className="history-data-table-framework">
              <thead>
                <tr className="history-table-header-row">
                  <th>Date</th><th>Client</th><th>Startup</th><th>Topic</th><th>Duration</th><th>Amount</th><th>Rating</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx} className="history-table-body-row">
                    <td>{row.date}</td>
                    <td><strong>{row.clientName}</strong></td>
                    <td>{row.clientCompany}</td>
                    <td>{row.topic}</td>
                    <td>{row.duration}</td>
                    <td>{row.status === "cancelled" ? "—" : `₹${Number(row.price).toLocaleString('en-IN')}`}</td>
                    <td>{row.rating ? `${"★".repeat(Math.round(row.rating))}${"☆".repeat(5 - Math.round(row.rating))}` : "—"}</td>
                    <td><span className={`history-status-pill status-${row.status}`}>{row.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    /* ============================================================
       # REVIEWS
       Rating summary + star breakdown, and the live feed of
       client reviews/comments.
       ============================================================ */
    case "reviews": {
      // One fresh fetch per tab visit. The ref guard inside refreshReviews
      // blocks repeated calls from re-renders within the same visit.
      // resetAndRefreshReviews resets the ref then calls refreshReviews.
      if (typeof p.resetAndRefreshReviews === "function" && !loadingReviews) {
        p.resetAndRefreshReviews();
      }
      const breakdown = [5, 4, 3, 2, 1].map(star => ({
        star,
        count: reviews.filter(r => Math.round(Number(r.rating) || 0) === star).length
      }));
      const maxCount = Math.max(...breakdown.map(b => b.count), 1);

      return (
        <div className="reviews-tab-vertical-stack">
          <div className="reviews-summary-card">
            <div className="reviews-summary-left">
              <h2>{stats.rating > 0 ? stats.rating.toFixed(1) : "—"}</h2>
              <div className="reviews-summary-stars">{stats.rating > 0 ? "★".repeat(Math.round(stats.rating)) + "☆".repeat(5 - Math.round(stats.rating)) : "No ratings yet"}</div>
              <p>Based on {stats.reviewsCount || 0} review{stats.reviewsCount === 1 ? "" : "s"}</p>
            </div>
            <div className="reviews-summary-bars">
              {breakdown.map(b => (
                <div key={b.star} className="reviews-summary-bar-row">
                  <span>{b.star} ★</span>
                  <div className="reviews-summary-bar-track"><div className="reviews-summary-bar-fill" style={{ width: `${(b.count / maxCount) * 100}%` }} /></div>
                  <span className="reviews-summary-bar-count">{b.count}</span>
                </div>
              ))}
            </div>
          </div>

          {loadingReviews ? (
            <p className="reviews-loading-indicator">Loading reviews…</p>
          ) : reviews.length === 0 ? (
            <div className="reviews-empty-state-card">
              <FiStar className="empty-star-icon" />
              <h4>No ratings or reviews yet</h4>
              <p>Once clients complete sessions with you, their ratings and feedback will appear here.</p>
            </div>
          ) : (
            <div className="reviews-feed-container">
              {reviews.map((r, i) => (
                <div key={i} className="reviews-item-feedback-card">
                  <div className="review-card-top-row">
                    <div className="review-card-identity">
                      <div className="review-card-avatar" style={{ background: avatarColorOf(r.clientName) }}>{initialsOf(r.clientName)}</div>
                      <div>
                        <strong>{r.clientName || "Client"}</strong>
                        <span className="review-card-company">{r.startupName || r.companyName || ""}</span>
                      </div>
                    </div>
                    <div className="review-card-right">
                      <span className="review-stars-badge">{"★".repeat(Math.round(Number(r.rating) || 0))}{"☆".repeat(5 - Math.round(Number(r.rating) || 0))}</span>
                      {r.createdAt && <span className="review-timestamp-label">{r.createdAt?.toDate?.()?.toLocaleDateString("en-IN") || ""}</span>}
                    </div>
                  </div>
                  {r.comment && <p className="reviews-item-comment-quote">"{r.comment}"</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    /* ============================================================
       # NOTIFICATIONS
       Generated feed of booking / session / payout alerts.
       ============================================================ */
    case "notifications": {
      const filteredNotifications = notificationFilter === "unread"
        ? notifications.filter(n => n.unread)
        : notifications;
      const unreadTotal = notifications.filter(n => n.unread).length;

      return (
        <div className="notifications-tab-stack-layout">
          <div className="notifications-card-shell">
            <div className="notifications-header-row">
              <h3>Notifications {unreadTotal > 0 && <span className="noti-new-badge">{unreadTotal} new</span>}</h3>
              <button
                className="noti-mark-all-read-btn"
                onClick={markAllNotificationsRead}
                disabled={unreadTotal === 0}
              >
                <FiCheckCircle /> Mark all as read
              </button>
            </div>

            <div className="noti-filter-tabs-row">
              <button
                className={`noti-filter-tab-btn ${notificationFilter === "all" ? "active" : ""}`}
                onClick={() => setNotificationFilter("all")}
              >
                All {notifications.length > 0 && <span className="noti-filter-count">{notifications.length}</span>}
              </button>
              <button
                className={`noti-filter-tab-btn ${notificationFilter === "unread" ? "active" : ""}`}
                onClick={() => setNotificationFilter("unread")}
              >
                Unread {unreadTotal > 0 && <span className="noti-filter-count">{unreadTotal}</span>}
              </button>
            </div>

            {filteredNotifications.length === 0 ? (
              <p className="no-notifications-label">
                {notificationFilter === "unread" ? "You're all caught up — no unread notifications." : "You're all caught up — no notifications right now."}
              </p>
            ) : (
              filteredNotifications.map((n) => {
                const meta = NOTI_ICON[n.type] || NOTI_ICON.booking;
                return (
                  <div
                    key={n.id}
                    className={`notifications-alert-card-item noti-clickable ${n.unread ? "unread-glow" : ""}`}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <div className="noti-icon-badge" style={{ background: meta.bg, color: meta.fg }}>{meta.icon}</div>
                    <div className="noti-body">
                      <strong>{n.title}</strong>
                      <p className="noti-msg-body">{n.message}</p>
                      <span className="noti-time-tag">{n.time}</span>
                    </div>
                    {n.unread && <span className="noti-unread-dot" aria-hidden="true"></span>}
                  </div>
                );
              })
            )}
          </div>

          {selectedNotification && (
            <div className="reschedule-modal-overlay" onClick={closeNotificationDetail}>
              <div className="reschedule-modal-card" onClick={(e) => e.stopPropagation()}>
                {(() => {
                  const meta = NOTI_ICON[selectedNotification.type] || NOTI_ICON.booking;
                  return (
                    <>
                      <div className="noti-detail-icon-badge" style={{ background: meta.bg, color: meta.fg }}>{meta.icon}</div>
                      <h3 className="reschedule-modal-title">{selectedNotification.title}</h3>
                      <p className="reschedule-modal-subtitle">{selectedNotification.time}</p>
                      <p className="noti-detail-message">{selectedNotification.message}</p>
                      <div className="reschedule-modal-actions">
                        {selectedNotification.bookingId && (
                          <button
                            className="reschedule-cancel-btn"
                            onClick={() => { closeNotificationDetail(); setActiveTab("calls"); }}
                          >
                            View Booking
                          </button>
                        )}
                        <button className="reschedule-confirm-btn" onClick={closeNotificationDetail}>Close</button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      );
    }

    /* ============================================================
       # SETTINGS
       Notification preferences, privacy settings, language, and
       the danger zone (deactivate / delete account).
       ============================================================ */
    case "settings": {
      return (
        <div className="settings-tab-narrow-wrapper">
          <div className="info-block-card">
            <div className="settings-section-head">
              <div className="settings-section-icon"><FiBell /></div>
              <div><h3 className="settings-preference-card-title">Notification Preferences</h3><p>Choose what you want to be notified about</p></div>
            </div>
            {[
              ["newBookings", "New Booking Requests", "When a client books a consultation"],
              ["sessionReminders", "Session Reminders", "15 minutes before each consultation"],
              ["payoutNotifications", "Payout Notifications", "When earnings are credited to your account"],
              ["newReviews", "New Reviews", "When a client leaves a review"],
              ["platformUpdates", "Platform Updates", "SORTED news and feature announcements"],
            ].map(([key, label, sub]) => (
              <label key={key} className="settings-preference-toggle-row">
                <div><strong>{label}</strong><p>{sub}</p></div>
                <input type="checkbox" checked={!!settingsForm[key]} onChange={(e) => handleSettingsChange(key, e.target.checked)} className="toggle-switch-input" />
              </label>
            ))}
          </div>

          <div className="info-block-card">
            <div className="settings-section-head">
              <div className="settings-section-icon green"><FiShield /></div>
              <div><h3 className="settings-preference-card-title">Privacy Settings</h3><p>Control your profile visibility</p></div>
            </div>
            {[
              ["publicProfile", "Public Profile", "Show your profile to entrepreneurs on SORTED"],
              ["showReviews", "Show Reviews", "Allow clients to see your past ratings"],
            ].map(([key, label, sub]) => (
              <label key={key} className="settings-preference-toggle-row">
                <div><strong>{label}</strong><p>{sub}</p></div>
                <input type="checkbox" checked={!!settingsForm[key]} onChange={(e) => handleSettingsChange(key, e.target.checked)} className="toggle-switch-input" />
              </label>
            ))}
          </div>

          <div className="info-block-card">
            <div className="settings-section-head">
              <div className="settings-section-icon amber"><FiGlobe /></div>
              <div><h3 className="settings-preference-card-title">Language &amp; Region</h3><p>Set your preferred language</p></div>
            </div>
            <label className="bank-form-input-label">Language</label>
            <select value={settingsForm.language} onChange={(e) => handleSettingsChange("language", e.target.value)} className="dashboard-input-control-styled">
              <option>English</option>
              <option>Hindi</option>
            </select>
          </div>

          <div className="info-block-card settings-preference-danger-card">
            <div className="settings-section-head">
              <div className="settings-section-icon red"><FiAlertTriangle /></div>
              <div><h3 className="settings-danger-card-title">Danger Zone</h3><p>Irreversible account actions</p></div>
            </div>
            <button onClick={handleDeactivateAccount} className="danger-zone-row-btn">
              <div><strong>Deactivate Account</strong><p>Temporarily hide your profile from SORTED (your bank details will be cleared and you'll need to re-verify them next time you sign in)</p></div>
              <span>›</span>
            </button>
            <button onClick={() => handleDeleteAccount()} className="danger-zone-row-btn">
  <div><strong>Delete Account</strong><p>Permanently delete all your data from SORTED</p></div>
  <span>›</span>
</button>
          </div>

          <button onClick={handleSaveAllSettings} disabled={isSavingSettings} className="save-all-settings-btn">
            {isSavingSettings ? "Saving..." : "Save All Settings"}
          </button>

          {/* FIRST POP-UP: ARE YOU SURE NOTICE */}
          {deleteConfirmStep === 1 && (
            <div className="delete-overlay-active">
              <div className="reschedule-modal-card">
                <h3 className="reschedule-modal-title" style={{ color: "#ef4444" }}>
                  Delete Account
                </h3>
                <p className="reschedule-modal-subtitle">
                  Are you sure you want to delete your account? This action cannot be undone.
                </p>
                <div className="reschedule-modal-actions">
                  <button onClick={() => setDeleteConfirmStep(0)} className="reschedule-cancel-btn">
                    Cancel
                  </button>
                  <button 
                    onClick={() => handleDeleteAccount(2)} 
                    className="reschedule-confirm-btn"
                    style={{ backgroundColor: "#ef4444", borderColor: "#ef4444", color: "#fff" }}
                  >
                    Proceed
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* SECOND POP-UP: FINAL YES OR NO CHOICE */}
          {deleteConfirmStep === 2 && (
            <div className="delete-overlay-active">
              <div className="reschedule-modal-card">
                <h3 className="reschedule-modal-title" style={{ color: "#ef4444" }}>
                  Final Confirmation
                </h3>
                <p className="reschedule-modal-subtitle">
                  Permanently wipe all your dashboard data from SORTED? Click Yes to delete, or No to go back.
                </p>
                <div className="reschedule-modal-actions">
                  <button onClick={() => setDeleteConfirmStep(0)} className="reschedule-cancel-btn">
                    No
                  </button>
                  <button 
                    onClick={() => handleDeleteAccount("FINAL_DELETE")} 
                    className="reschedule-confirm-btn"
                    style={{ backgroundColor: "#ef4444", borderColor: "#ef4444", color: "#fff" }}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          )}
          
        </div>
      );
    }

    default:
      return <div className="dashboard-default-fallback-panel">Page not found.</div>;
  }
};

export default ExpertDashboard;